// Portland, "Neon stag over the Willamette": a drizzly autumn night on the Eastbank Esplanade, about 300 m north of
// the Burnside Bridge's east end (45.5246 N, 122.6655 W), looking west-south-west (about 235°) across the Willamette
// at Old Town. The "Portland Oregon" sign (the White Stag sign, 1940, lettered "Portland Oregon" since 2010) stands on
// the White Stag Building's roof at the bridge's west end and faces east, to the traffic coming over the bridge, so
// from here it is seen about 33° off its face and reads. Left to right, as seen from the Esplanade: the Burnside
// Bridge going away from the viewer (east pier and its tower at about 202°, the bascule span, the west pier and its
// tower at 214°, the west approach landing at 227°), downtown's towers behind it (the KOIN Center at 217°, the Wells
// Fargo Center at 223°) and the West Hills' television masts on the ridge (216°); the sign (237°) above the trees of
// Waterfront Park; the US Bancorp Tower ("Big Pink", 242°) rising behind it; Old Town's brick blocks along the
// waterfront; Union Station's clock tower (293°, pulled in to the right edge). The 110° is compressed into the frame
// without changing the order, and the sign is drawn about twice its true size next to the bridge, so it reads.
// Mt Hood is not in the frame: it stands 78 km away at about 102°, behind anyone who can read the east-facing sign.
// Back to front: deep blue sky, a moon, stars -> the West Hills (firs on the ridge, lit houses, red-lit masts) ->
// downtown's towers -> the Burnside Bridge (concrete piers, steel trusses, the bascule span's shallow arch, two
// Italian Renaissance tower houses with green copper roofs) -> the White Stag Building and its sign, Big Pink, Old
// Town, Waterfront Park's trees, globe lamps and seawall -> the Willamette (dark green, the neon mirrored in it) ->
// the Esplanade's floating walkway: its rail, rust-red piles, a lamp, a bench -> a food cart on the right (the cozy
// corner) with its string lights and picnic table.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, rect, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 157;                     // far waterline: the foot of Waterfront Park's seawall
const KERB = 194;                   // top of the floating walkway's edge: the Willamette shows from WL down to it
const FEET = 205;                   // people on the walkway stand on this row (above the bottom 63 rows)
const MOON = { x: 150, y: 31, r: 6 };
// the sign: drawn from an 84 x 86 design (sign-local units) scaled by K; x is the outline's left edge, y the row of the
// antler tips
const K = 1.15;
const SIGN = { x: 232, y: 30, w: Math.round(84 * K), h: Math.round(86 * K) };
const SU = u => SIGN.x + Math.round(u * K), SV = v => SIGN.y + Math.round(v * K);   // sign-local units -> scene pixels
const ROOF = SIGN.y + SIGN.h;       // the White Stag Building's roof, where the sign's legs stand
// the Burnside Bridge, going away from the viewer: deck top at the left edge, at the east and west piers, and where
// it goes behind the White Stag Building; the two tower houses stand on the piers
const BR = { x0: -4, y0: 97, xe: 74, ye: 111, xw: 152, yw: 123, x1: 232, y1: 134 };
const BIGPINK = { x0: 345, x1: 366, top: 22 };
const BB = { x: 374, y: 92, w: 50, h: 20 };       // the rooftop billboard's face in Old Town
const CART = { x0: 392, x1: 452, top: 160, base: 206 };   // the food cart on the Esplanade (the cozy corner)
const LAMP = { x: 34, y: 140 };                   // the Esplanade lamp's lantern (left, near the viewer)

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line through a put(x, y) function
function bres(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
// pick from a dark-to-light ramp with ordered dither
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
// distance from (px, py) to the segment (ax, ay)-(bx, by)
function segDist(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay, l = dx * dx + dy * dy, u = l ? clamp(((px - ax) * dx + (py - ay) * dy) / l, 0, 1) : 0;
  return Math.hypot(px - ax - u * dx, py - ay - u * dy);
}

/* ================= Sign font: FONT3 with wide M, W, N, K, V (FONT3's read as H and Π at 1x) ================= */
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: deep blue, a warm city haze low over the hills ================= */
const SKY = ['#04071a', '#060b22', '#08102b', '#0b1534', '#0e1a3d', '#121f46', '#17254e', '#1d2b55', '#26315a',
  '#30375c', '#3c3d5e', '#4a435d'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 150), 1.5) * 0.82;
    v += Math.max(0, 1 - Math.hypot((i - 250) / 260, (y - 150) / 70)) * 0.2;      // downtown's glow under the hills
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 50)) * 0.12; // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.6], [1, 2, 1.4], [2, -2, 1], [-1, 3, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fffbea' : '#f2ead2';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c8bc9e' : '#e6dcc0';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#bfb498' : '#d6ccb0';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(53);
  while (STARS.length < 44) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 84);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    if (x > SIGN.x - 6 && x < SIGN.x + SIGN.w + 6 && y > SIGN.y - 4) continue;   // none inside the sign
    STARS.push({ x, y, k: 12 + Math.floor(r() * 50), ph: r(), b: y < 50 ? 0.45 + r() * 0.5 : 0.25 + r() * 0.35, big: r() < 0.1 });
  }
}

/* ================= The West Hills: a long dark ridge of firs, lit houses, the television masts ================= */
// highest in the south-west (Council Crest and Healy Heights, on the left) and lower to the north-west (Forest Park)
const ridgeY = x => 97 + x * 0.035 + 4 * Math.sin(x * 0.019 + 0.6) + 2.2 * Math.sin(x * 0.051 + 1.3) - 9 * Math.exp(-(((x - 118) / 46) ** 2));
const MASTS = [{ x: 108, top: 52, w: 3 }, { x: 121, top: 47, w: 3 }, { x: 133, top: 58, w: 2 }];   // the TV and radio masts
function renderHills() {
  const p = painter(W, H), put = p.put, r = rng(19);
  const far = x => ridgeY(x) - 7 + 3 * Math.sin(x * 0.03 + 2);                 // a farther, paler ridge behind
  for (let x = 0; x < W; x++) {
    for (let y = Math.round(far(x)); y < 160; y++) put(x, y, bayer(x, y) < 0.5 ? '#151c34' : '#131a30');
    const top = Math.round(ridgeY(x));
    for (let y = top; y < 160; y++) {
      const v = (y - top) / 34 + (bayer(x, y) - 0.5) * 0.25;
      put(x, y, v < 0.08 ? '#1c2a32' : v < 0.45 ? '#121c26' : '#0e1620');
    }
  }
  // fir tips along the skyline, a pointed tree every few columns
  for (let x = 0; x < W; x += 2 + Math.floor(r() * 3)) {
    const top = Math.round(ridgeY(x)), h = 2 + Math.floor(r() * 4);
    for (let k = 0; k < h; k++) { put(x, top - k, '#121c26'); if (k < h - 2) { put(x - 1, top - k, '#121c26'); put(x + 1, top - k, '#121c26'); } }
  }
  // lit houses scattered on the slopes, denser low down and towards the city
  for (let k = 0; k < 120; k++) {
    const x = Math.floor(r() * W), top = ridgeY(x), y = Math.round(top + 3 + Math.pow(r(), 0.7) * 30);
    if (y > 150) continue;
    put(x, y, r() < 0.7 ? '#d8a050' : '#f2d08a');
    if (r() < 0.25) put(x + 1, y, '#a87038');
  }
  // the masts: lattice towers tapering to a point, guy wires faint against the sky
  for (const m of MASTS) {
    const base = Math.round(ridgeY(m.x)) + 1;
    for (let y = m.top; y < base; y++) {
      const hw = Math.round((m.w - 1) * (y - m.top) / (base - m.top));
      put(m.x, y, '#2a3448');
      if (hw > 0) { put(m.x - hw, y, (y & 1) ? '#222a3c' : '#1a2232'); put(m.x + hw, y, (y & 1) ? '#1a2232' : '#222a3c'); }
    }
  }
  return p.done();
}

/* ================= Downtown behind the bridge: the KOIN Center, the Wells Fargo Center, mid-rises ================= */
const NIGHT = '#141a3a';
const WARMW = ['#ffd890', '#f6bc66', '#ec9c4c'];
// a tower block: columns x0..x1 from row top down to row 160 (hidden lower down), its face ramp lit from the left,
// windows in a grid, a share of them lit
function block(p, x0, x1, top, face, { win = 0.3, seed = 1, wy = 3, wx = 2, edge } = {}) {
  for (let y = top; y < 160; y++) for (let x = x0; x <= x1; x++) {
    let col = x === x0 ? (edge ?? face[0]) : x === x1 ? face[2] : face[1];
    if (y > top + 1 && (y - top) % wy === 1 && (x - x0) % wx === 1 && x < x1) col = hash(seed, x, y) < win ? WARMW[(x + y) % 3] : face[2];
    p.put(x, y, col);
  }
}
function renderDowntown() {
  const p = painter(W, H), put = p.put, r = rng(7);
  // Skidmore and the Saturday Market's blocks south of the bridge, low brick, seen through the span when it opens
  for (const [x0, x1, top, k] of [[82, 96, 122, 0], [96, 108, 126, 1], [108, 124, 119, 2], [124, 138, 124, 3], [136, 150, 117, 4]])
    block(p, x0, x1, top, k & 1 ? ['#6a4a40', '#54383a', '#3e2a2e'] : ['#5a4a4a', '#463a3e', '#342a30'], { win: 0.5, seed: 60 + k, wy: 4, wx: 3 });
  // mid-rises of the inner city between the bridge's towers and the sign
  for (const [x0, x1, top, k] of [[138, 150, 98, 0], [160, 171, 92, 1], [183, 196, 84, 2], [212, 224, 90, 3], [226, 236, 100, 4], [150, 160, 104, 5]])
    block(p, x0, x1, top, k & 1 ? ['#3a3c58', '#2c2e48', '#22243a'] : ['#3e3a50', '#302c42', '#242036'], { win: 0.35, seed: 30 + k });
  // the Wells Fargo Center: Portland's tallest, a dark flat-topped slab with a lit band at its crown
  block(p, 197, 210, 50, ['#4a4a62', '#2e2e46', '#22223a'], { win: 0.22, seed: 41, wy: 3, wx: 2 });
  for (let x = 197; x <= 210; x++) { put(x, 50, '#8a8aa8'); put(x, 51, x & 1 ? '#c8d0e8' : '#9aa4c4'); put(x, 52, '#3a3a56'); }
  // the KOIN Center: brick and tan stepped tower under its pyramid crown and spire, the crown lit
  block(p, 174, 186, 70, ['#8a5e4e', '#6a4a40', '#4e3632'], { win: 0.3, seed: 43, wy: 3, wx: 2 });
  for (let y = 70; y < 76; y++) { put(173, y, '#5a4038'); put(187, y, '#3e2c2a'); }
  for (let k = 0; k < 9; k++) {                                   // the pyramid crown: blue-lit glass in steps
    const hw = 6 - Math.floor(k * 0.7), y = 69 - k;
    for (let x = 180 - hw; x <= 180 + hw; x++) put(x, y, x === 180 - hw ? '#a8c8f0' : x > 180 + hw - 2 ? '#3a5280' : k % 3 === 2 ? '#7ea0d8' : '#5a7ab8');
  }
  for (let y = 54; y < 61; y++) put(180, y, y < 57 ? '#9aa4b8' : '#6a7488');
  // the US Bancorp Tower ("Big Pink"): rose granite and bronze glass, a parallelogram in plan, so one broad face
  // and one sharp narrow one; dark at night, a few floors lit, red lamps on its roof
  const { x0, x1, top } = BIGPINK;
  for (let y = top; y < 160; y++) for (let x = x0; x <= x1; x++) {
    const narrow = x > x1 - 5, v = (y - top) / 130 + (bayer(x, y) - 0.5) * 0.1;
    let col = narrow ? (x === x1 ? '#2a1c26' : '#3e2a34') : x === x0 ? '#7a565e' : v < 0.4 ? '#563a44' : '#4a323c';
    if (!narrow && x > x0 && (x - x0) % 2 === 0) col = mixHex(col, '#2a1a24', 0.45);            // the mullions
    if (!narrow && x > x0 && (y - top) % 3 === 2 && hash(x >> 1, y, 47) < 0.12) col = hash(x, y, 48) < 0.5 ? '#d89a60' : '#b87a50';  // lit floors
    put(x, y, col);
  }
  for (let x = x0; x <= x1; x++) put(x, top, '#8a6a70');
  void r;
  return p.done();
}

/* ================= Old Town along the waterfront, right of the sign ================= */
// brick blocks of four to six storeys, the water tank on its legs, the cast-iron Bickel Block, Union Station's clock
// tower at the right edge with the red neon on its top
const BRICK = [['#9a5a44', '#7a4434', '#5a3028'], ['#a87050', '#86563e', '#5e3c2e'], ['#8a5a50', '#6a443e', '#4a302e'],
  ['#b89870', '#94785a', '#6a5440'], ['#7a5a62', '#5e444c', '#422e36']];
const TOWN = [   // x0, x1, roof row, ramp, storey height
  { x0: 214, x1: 230, top: 122, f: 0, st: 5 }, { x0: 333, x1: 349, top: 125, f: 3, st: 5 },
  { x0: 362, x1: 384, top: 120, f: 1, st: 5 }, { x0: 384, x1: 412, top: 114, f: 2, st: 5 }, { x0: 412, x1: 436, top: 118, f: 4, st: 5 },
  { x0: 436, x1: 452, top: 112, f: 0, st: 5 }, { x0: 452, x1: 480, top: 120, f: 3, st: 5 },
];
const UNION = { x: 463, top: 74 };
function renderOldTown() {
  const p = painter(W, H), put = p.put;
  // Union Station's clock tower behind the blocks: brick, a pyramid roof, a lit clock, the red neon band on top
  const { x: ux, top: ut } = UNION;
  for (let y = ut + 8; y < 130; y++) for (let x = ux - 3; x <= ux + 3; x++) put(x, y, x === ux - 3 ? '#a87058' : x === ux + 3 ? '#5a3428' : '#86503e');
  for (let k = 0; k < 7; k++) for (let x = ux - 3 + (k >> 1); x <= ux + 3 - (k >> 1); x++) put(x, ut + 7 - k, x <= ux ? '#6a5a50' : '#4a3e38');
  put(ux, ut, '#8a7a6a'); put(ux, ut - 1, '#8a7a6a');
  for (let x = ux - 2; x <= ux + 2; x++) put(x, ut + 9, '#ff5a3a');                    // GO BY TRAIN, a red line at this distance
  for (const [i, j] of [[0, -1], [-1, 0], [1, 0], [0, 1], [0, 0]]) put(ux + i, ut + 15 + j, i || j ? '#f2e2b8' : '#5a4a3a');
  for (const b of TOWN) {
    const F = BRICK[b.f];
    for (let y = b.top; y < 152; y++) for (let x = b.x0; x < b.x1; x++) {
      let col = x === b.x0 ? F[0] : x === b.x1 - 1 ? F[2] : F[1];
      if (y === b.top) col = mixHex(F[0], '#e8d8c0', 0.3);                                // the cornice
      if (y > b.top + 2 && (y - b.top) % b.st >= 2 && (y - b.top) % b.st <= 3 && (x - b.x0) % 4 >= 1 && (x - b.x0) % 4 <= 2 && x < b.x1 - 1)
        col = hash(b.x0, x >> 2, y >> 2) < 0.45 ? WARMW[(x >> 2) % 3] : '#1e1a2c';
      put(x, y, col);
    }
  }
  // the wooden water tank on its legs, on the roof right of the sign
  const tx = 337, tt = 110;
  for (let y = tt; y < tt + 10; y++) for (let x = tx - 5; x <= tx + 5; x++) put(x, y, (y - tt) % 4 === 3 ? '#3a2a24' : x < tx - 2 ? '#7a5a44' : x < tx + 3 ? '#5e4434' : '#42302a');
  for (let k = 0; k < 3; k++) for (let x = tx - 5 + k; x <= tx + 5 - k; x++) put(x, tt - 1 - k, k === 2 ? '#3a2a24' : '#4e3a30');
  for (const lx of [tx - 4, tx, tx + 4]) for (let y = tt + 10; y < 126; y++) put(lx, y, '#24202a');
  bres(put, tx - 4, tt + 11, tx + 4, 124, '#24202a'); bres(put, tx + 4, tt + 11, tx - 4, 124, '#24202a');
  return p.done();
}

/* ================= The Burnside Bridge (1926), going away from the viewer ================= */
// Real: a double-leaf Strauss bascule, main span 77 m between two massive concrete piers that hold the counterweights,
// deck 20 m over the river, steel deck-truss approaches, and two Italian Renaissance tower houses (cream walls, a
// row of windows, green copper hipped roofs) on the piers. Seen from 300 m north of it, it runs from the left edge
// (its east end, nearest) to the west bank, where it passes behind the White Stag Building.
const deckY = x => x < BR.xe ? lerp(BR.y0, BR.ye, (x - BR.x0) / (BR.xe - BR.x0)) : x < BR.xw ? lerp(BR.ye, BR.yw, (x - BR.xe) / (BR.xw - BR.xe))
  : lerp(BR.yw, BR.y1, (x - BR.xw) / (BR.x1 - BR.xw));
const deckT = x => Math.round(lerp(8, 4, clamp((x - BR.x0) / (BR.x1 - BR.x0), 0, 1)));   // the deck's thickness
const PIERS = [{ x0: 62, x1: 86, foot: 166 }, { x0: 144, x1: 159, foot: 161 }];
const TOWERS = [{ x: 74, w: 11, h: 16, roof: 6 }, { x: 152, w: 7, h: 11, roof: 4 }];
const STEEL = ['#6e665c', '#4e4842', '#3a3632', '#2a2826'];
const CONC = ['#c8c0ae', '#a49c8c', '#7e786c', '#585450'];
function renderBridge() {
  const p = painter(W, H), put = p.put;
  // the east bank under the east approach: riprap down into the river, the Esplanade's edge on top
  for (let x = 0; x < 70; x++) {
    const top = Math.round(160 + x * 0.06);
    for (let y = top; y < 176 - Math.round(x * 0.12); y++) put(x, y, y === top ? '#4a4c58' : hash(x >> 1, y >> 1, 5) < 0.3 ? '#2e3038' : '#24262e');
  }
  // the approaches: steel deck trusses under the deck (a Warren truss with verticals), on concrete bents
  const truss = (xa, xb, depth) => {
    for (let x = xa; x < xb; x++) {
      const yb = Math.round(deckY(x)) + deckT(x), d = Math.round(depth(x));
      put(x, yb + d, STEEL[1]); put(x, yb + d - 1, STEEL[2]);
    }
    const panel = x => Math.max(5, Math.round(deckT(x) * 1.6));
    for (let x = xa; x < xb;) {
      const pw = panel(x), x2 = Math.min(xb - 1, x + pw);
      if (x2 <= x) break;
      const yb = Math.round(deckY(x)) + deckT(x), yb2 = Math.round(deckY(x2)) + deckT(x2);
      bres(put, x, yb, x, yb + Math.round(depth(x)), STEEL[1]);
      bres(put, x, yb + Math.round(depth(x)), x2, yb2, STEEL[0]);
      bres(put, x, yb, x2, yb2 + Math.round(depth(x2)), STEEL[2]);
      x = x2;
    }
  };
  truss(BR.x0, PIERS[0].x0, x => lerp(15, 10, (x - BR.x0) / (PIERS[0].x0 - BR.x0)));
  truss(PIERS[1].x1, BR.x1, x => lerp(7, 4, (x - PIERS[1].x1) / (BR.x1 - PIERS[1].x1)));
  for (const [bx, foot, w] of [[6, 172, 5], [36, 168, 4], [178, 158, 3], [204, 157, 2]]) {
    const top = Math.round(deckY(bx)) + deckT(bx) + 2;
    for (let y = top; y < foot; y++) for (let x = bx; x < bx + w; x++) put(x, y, x === bx ? CONC[1] : CONC[3]);
  }
  // the piers: massive concrete, the face towards the viewer lit, the far side shaded, a band at the waterline
  for (const P of PIERS) {
    const top = Math.round(deckY(P.x0)) + deckT(P.x0) - 1;
    for (let y = top; y < P.foot; y++) for (let x = P.x0; x < P.x1; x++) {
      const u = (x - P.x0) / (P.x1 - P.x0), v = u * 0.8 + (bayer(x, y) - 0.5) * 0.15 + (y > P.foot - 4 ? 0.3 : 0);
      let col = ramp(CONC, 1 - v, x, y);
      if (y - top === 3 || y - top === 4) col = u < 0.7 ? CONC[0] : CONC[2];                        // the coping
      if ((y - top) > 6 && (y - top) % 9 === 0 && u > 0.1 && u < 0.9) col = CONC[2];                 // pour lines
      put(x, y, col);
    }
    for (let x = P.x0 - 1; x <= P.x1; x++) put(x, top + 2, CONC[1]);
  }
  // the deck: its concrete edge, the balustrade on top, lit from the lamps
  for (let x = BR.x0; x < BR.x1; x++) {
    if (x >= SPAN.x0 && x < SPAN.x1) continue;                    // the leaves are drawn per frame
    const y = Math.round(deckY(x)), t = deckT(x), bal = t >= 6 ? 2 : 1;
    for (let k = 0; k < t; k++) put(x, y + k, k === 0 ? '#bab2a0' : k === 1 ? CONC[2] : k === t - 1 ? CONC[3] : mixHex(CONC[2], CONC[3], 0.4));
    for (let k = 1; k <= bal; k++) put(x, y - k, k === bal ? '#aaa290' : ((x % 3) ? '#7a7466' : '#3a3a44'));
  }
  // the tower houses on the piers: cream stucco, a band of windows, a copper roof gone green, a finial
  for (const T of TOWERS) {
    const base = Math.round(deckY(T.x)) - 1, x0 = T.x - (T.w >> 1), x1 = x0 + T.w - 1, top = base - T.h;
    for (let y = top; y <= base; y++) for (let x = x0; x <= x1; x++) {
      const u = (x - x0) / (T.w - 1);
      let col = u < 0.2 ? '#f0e8d4' : u < 0.7 ? '#d8ccb0' : '#a89c84';
      if (y === top + 1 || y === base - Math.round(T.h * 0.4)) col = '#8e8270';                    // cornice and string course
      if (y >= top + 3 && y <= top + 3 + Math.round(T.h * 0.25) && x > x0 && x < x1 && (x - x0) % 2 === 1) col = y === top + 3 ? '#ffe2b0' : '#f2b07a';
      put(x, y, col);
    }
    for (let k = 0; k < T.roof; k++) {
      const hw = (T.w >> 1) + 1 - Math.round(k * ((T.w >> 1) + 1) / T.roof);
      for (let x = T.x - hw; x <= T.x + hw; x++) put(x, top - 1 - k, x < T.x - hw / 2 ? '#7ec4a8' : x < T.x + hw / 2 ? '#5aa088' : '#3e7a68');
    }
    put(T.x, top - 1 - T.roof, '#8a8070'); put(T.x, top - 2 - T.roof, '#c8b890');
  }
  return p.done();
}
// the deck's lamp posts along its north edge (x, pole height), smaller as they go away
const BR_LAMPS = [[14, 11], [44, 10], [83, 8], [147, 7], [176, 6], [200, 5], [222, 4]];
// the bascule span between the piers: two leaves, each turning up about its pier, meeting mid-river
const SPAN = { x0: PIERS[0].x1, x1: PIERS[1].x0 }, SPAN_MID = (PIERS[0].x1 + PIERS[1].x0) / 2;
// one leaf's pixels at lift angle a (0 closed): balustrade on top, the deck, the girder under it, deepest at the pier
function leafPixels(put, side, a) {
  const px = side ? SPAN.x1 : SPAN.x0, dir = side ? -1 : 1, L = Math.abs(SPAN_MID - px), py = deckY(px);
  const slope = (deckY(SPAN_MID) - py) / L, n0 = Math.hypot(1, slope), th = -dir * a;
  const cx = dir / n0, cy = slope / n0, ux = cx * Math.cos(th) - cy * Math.sin(th), uy = cx * Math.sin(th) + cy * Math.cos(th);
  const nx = -dir * uy, ny = dir * ux, Lt = L * n0 + (side ? 0 : 0.5);
  for (let k = 0; k <= Lt; k += 0.5) {
    const xs = px + dir * k / n0, u = (xs - SPAN.x0) / (SPAN.x1 - SPAN.x0), t = deckT(xs), bal = t >= 6 ? 2 : 1;
    const d = Math.round(2 + 9 * (2 * u - 1) ** 2 * lerp(1, 0.75, u)), bx = px + ux * k, by = py + uy * k;
    for (let j = -bal; j < t + d; j += 0.5) {
      const ji = Math.floor(j);
      const col = ji < 0 ? (ji === -bal ? '#aaa290' : (Math.round(xs) % 3 ? '#7a7466' : '#3a3a44'))
        : ji < t ? (ji === 0 ? '#bab2a0' : ji === 1 ? CONC[2] : ji === t - 1 ? CONC[3] : mixHex(CONC[2], CONC[3], 0.4))
        : ji === t + d - 1 ? STEEL[0] : (Math.round(xs) + ji) % 5 === 0 ? STEEL[3] : STEEL[2];
      put(Math.round(bx + nx * j), Math.round(by + ny * j), col);
    }
  }
}

/* ================= The "Portland Oregon" sign on the White Stag Building ================= */
// Real (night photos 2010-2024; about 48 ft tall and 46 ft wide): a thin orange neon outline of the state of Oregon;
// "Portland" over "Oregon" in a brush script that climbs to the right, the letters filled with amber bulbs and
// edged in green neon; the white stag leaping to the right over the outline's top right, its body striped with white
// neon; "OLD TOWN" in red neon across a gap in the outline's bottom edge; a steel lattice behind it all.
// Measured on the frontal night photo: overall height (antler tips to OLD TOWN) 1.01 of the outline's width, the stag
// 0.42 of the width, "Portland" 0.84 of it, "OLD TOWN" 0.54. Drawn: 86 / 84 = 1.02, 35 / 84 = 0.42, 71 / 84 = 0.85,
// 45 / 84 = 0.54. All coordinates below are sign-local: u from the outline's left edge, v from the antler tips.
const OUTLINE = [   // the state, clockwise from the north-west corner (Astoria) round to the bottom-left
  [8, 14], [10, 13.3], [14, 13.4], [16, 15], [18, 17.5], [21, 20], [26, 20.4], [34, 20.6], [44, 20.6], [56, 20.4],
  [68, 20.4], [78, 20.4], [80, 22.5], [82.5, 25.5], [83.6, 26.4], [82.2, 29.5], [83.2, 33], [82.6, 40], [82.4, 60],
  [82.6, 81.6], [71, 81.6], null, [14.5, 81.6], [3.7, 81.6], [2.2, 76], [0.4, 69], [0.3, 64], [2.2, 56], [3.4, 53],
  [5, 47], [6.2, 41], [7.1, 33], [7.6, 22], [8, 14],
];
// brush-script glyphs as stroke centrelines, traced from the night photos: baseline y 0, x-height about 10, the caps
// and ascenders to -21..-24, glyph units of about a pixel; strokes are about 4 px wide, so counters stay small
const ell = (cx, cy, rx, ry, n = 14) => Array.from({ length: n + 1 }, (_, k) => [cx + rx * Math.cos(TAU * k / n), cy + ry * Math.sin(TAU * k / n)]);
const SCRIPT = {
  P: { adv: 14, s: [[[4, -22], [3, -12], [2, -4], [1, 0], [-1, 2]], [[-1, -15], [-1, -19], [2, -22], [6, -24], [10, -23], [12, -20], [11, -16], [7, -14], [3, -14]]] },
  o: { adv: 10, s: [ell(3, -5, 2.3, 4.4)] },
  r: { adv: 8, s: [[[1.3, -1.4], [0.9, -9]], [[0.9, -7], [2.2, -9.8], [3.4, -9.6]]] },
  t: { adv: 7, s: [[[3.8, -18], [2, -6], [2, -1.5], [3.4, 0], [6, -1]], [[1.6, -10.6], [6, -11]]] },
  l: { adv: 7, s: [[[0, -1], [3, -12], [5, -18.5], [5.4, -20.5], [4, -21], [2.6, -17.5], [1.4, -10], [1.2, -3], [2.6, 0], [5, -1]]] },
  a: { adv: 8, s: [ell(3, -5, 2.3, 4.4), [[5.6, -9.5], [5.4, -2], [6.4, 0], [8, -1]]] },
  n: { adv: 8, s: [[[0, 0], [0.4, -9.6]], [[0.4, -6.5], [2, -9.4], [3.8, -9.8], [5.2, -8], [5, -2], [6, 0], [7.5, -1]]] },
  d: { adv: 10, s: [ell(3, -5, 2.3, 4.4), [[5.6, -2], [6, -11], [7.2, -17], [8.4, -20.5], [7.4, -21.4], [6.4, -19.6]], [[5.6, -2], [6.6, 0], [8.5, -1]]] },
  O: { adv: 14, s: [ell(6.5, -7.4, 4.6, 10.6, 22), [[9.6, -16], [10.8, -17.4]]] },
  e: { adv: 8, s: [[[0, -2], [2.5, -4.5], [4.6, -7], [4.2, -9.4], [2.2, -10], [0.4, -7], [0.4, -2.5], [2, 0], [4.6, -0.8], [6.4, -2]]] },
  g: { adv: 8, s: [ell(3, -5, 2.3, 4.4), [[5.6, -10], [5.5, 0], [4.8, 6], [2.6, 10], [0, 9]]] },
};
// lay out a word: glyph units -> sign-local (u, v): italic slant, a baseline climbing to the right (a shear, so the
// strokes keep their slant), a scale
function scriptWord(word, u0, v0, { slant = 0.25, rise = 0.22, sc = 1 } = {}) {
  const segs = [];
  let gx = 0;
  for (const ch of word) {
    const G = SCRIPT[ch];
    for (const line of G.s) for (let k = 1; k < line.length; k++) {
      segs.push([line[k - 1], line[k]].map(([x, y]) => {
        const lx = (gx + x) * sc + (-y * sc) * slant;
        return [u0 + lx, v0 + y * sc - lx * rise];
      }));
    }
    gx += G.adv;
  }
  return segs;
}
const WORDS = [...scriptWord('Portland', 7.5, 55, { sc: 0.9 }), ...scriptWord('Oregon', 21.5, 72, { sc: 0.95 })];
// the stag, traced from the night photo: a silhouette (body, neck, head) and its legs, antlers and tail as tubes
const STAG_BODY = [[41.8, 19.0], [45.7, 17.6], [50, 16.0], [53.4, 14.4], [53.0, 10.8], [52.2, 7.6], [51.6, 6.2], [53.4, 6.4], [54.6, 5.2], [57.8, 5.6],
  [61.8, 7.0], [61.6, 8.4], [59.8, 9.2], [56.7, 9.2], [55.9, 10.4], [59.8, 13.1], [63.7, 17.4], [63.3, 19.8], [59.8, 21.7], [52, 22.9],
  [45.7, 22.9], [42.6, 21.0]];
const STAG_LIMBS = [
  [[61.8, 19.4], [66, 19.8], [70.2, 19.4], [71.2, 20.4]],     // fore leg reaching forward
  [[59.8, 21.4], [66.1, 22.1], [60.9, 27.6]],                 // fore leg folded back under
  [[42.6, 19.6], [39.1, 15.6], [38.6, 15.4]],                 // hind leg kicked up behind
  [[44.9, 22.4], [40.4, 29.2], [35.9, 35.8]],                 // hind leg stretched down to the hoof
];
// the antlers: thin curved tubes swept back, a tine off the long beam, the brow tine forward
const STAG_ANTLERS = [
  [[53.8, 6], [51, 3.4], [47.2, 1.6], [42.6, 0.8]], [[54.4, 5.4], [52.4, 2.8], [49.4, 1.2], [47.6, 0.2]],
  [[48.6, 2.2], [47.6, -0.4]], [[55, 5.2], [56.2, 2.8], [56.4, 0.6]], [[53, 6.4], [51, 5]],
];
const OLDTOWN_FONT = {   // a bold sans like the sign's own, two-pixel strokes, seven rows
  O: ['.####.', '##..##', '##..##', '##..##', '##..##', '##..##', '.####.'], L: ['##...', '##...', '##...', '##...', '##...', '##...', '#####'],
  D: ['#####.', '##..##', '##..##', '##..##', '##..##', '##..##', '#####.'], T: ['######', '..##..', '..##..', '..##..', '..##..', '..##..', '..##..'],
  W: ['##...##', '##...##', '##...##', '##.#.##', '##.#.##', '#######', '.##.##.'], N: ['##..##', '###.##', '###.##', '######', '##.###', '##.###', '##..##'],
};
function inPoly(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
const SG = { green: '#3cf070', greenD: '#18a848', bulb: '#ffb84a', bulbH: '#ffe9a8', bulbD: '#d27a26',
  orange: '#ff9a3a', orangeD: '#c8641e', white: '#f4fbff', stripe: '#c4d8ee', red: '#ff4a34', redH: '#ffb090', panel: '#2a1218' };
// the sign's own canvas (W x H, drawn in place) and a soft glow canvas drawn additively under it
const BULBS = [];                                   // the bulbs' pixels, for their shimmer
function renderSign() {
  const p = painter(W, H), put = p.put, { x: SX, y: SY, w: SW, h: SH } = SIGN;
  const at = (x, y, col) => put(SX + x, SY + y, col);               // x, y: pixels from the sign's corner
  // the steel lattice behind: posts, rails and braces, catching a little of the bulbs' light
  const latt = (u0, v0, u1, v1) => bres((x, y, c) => { if (!p.get(x, y)) put(x, y, c); }, SU(u0), SV(v0), SU(u1), SV(v1), '#2c3242');
  for (const u of [10, 26, 42, 58, 74]) latt(u, 24, u, 84);
  for (const v of [30, 46, 62, 76]) latt(8, v, 80, v);
  for (let k = 0; k < 4; k++) { latt(10 + k * 16, 30, 26 + k * 16, 46); latt(26 + k * 16, 46, 10 + k * 16, 62); latt(10 + k * 16, 62, 26 + k * 16, 76); }
  // the sign's legs down to the roof
  for (const u of [12, 40, 70]) for (let y = SV(82); y < ROOF; y++) { put(SU(u), y, '#3a3e4c'); put(SU(u) + 1, y, '#22242e'); }
  // the Oregon outline: one orange neon tube
  for (let k = 1; k < OUTLINE.length; k++) {
    const a = OUTLINE[k - 1], b = OUTLINE[k];
    if (!a || !b) continue;
    bres((x, y) => put(x, y, SG.orange), SU(a[0]), SV(a[1]), SU(b[0]), SV(b[1]));
  }
  // the words: amber bulbs in the stroke, green neon round it (stroke widths in pixels, whatever the scale)
  for (let y = Math.round(14 * K); y <= Math.round(80 * K); y++) for (let x = Math.round(2 * K); x <= SW; x++) {
    const u = (x + 0.5) / K, v = (y + 0.5) / K;
    let d = 9;
    for (const [[ax, ay], [bx, by]] of WORDS) { const dd = segDist(u, v, ax, ay, bx, by); if (dd < d) d = dd; }
    d *= K;
    if (d <= 0.95) { at(x, y, hash(x, y, 3) < 0.2 ? SG.bulbH : hash(x, y, 4) < 0.8 ? SG.bulb : SG.bulbD); BULBS.push(SX + x, SY + y); }
    else if (d <= 1.7) at(x, y, d < 1.45 ? SG.green : SG.greenD);
  }
  // the stag: striped white neon body, white tube limbs and antlers, a bright rim
  const inB = (x, y) => inPoly(STAG_BODY, x / K, y / K);
  for (let y = -3; y <= Math.round(37 * K); y++) for (let x = Math.round(33 * K); x <= Math.round(74 * K); x++) {
    const cx = x + 0.5, cy = y + 0.5;
    let d = 9;
    for (const L of STAG_LIMBS) for (let k = 1; k < L.length; k++) d = Math.min(d, segDist(cx / K, cy / K, ...L[k - 1], ...L[k]) * K);
    for (const L of STAG_ANTLERS) for (let k = 1; k < L.length; k++) d = Math.min(d, segDist(cx / K, cy / K, ...L[k - 1], ...L[k]) * K + 0.3);
    if (inB(cx, cy)) {
      const rim = !inB(cx - 1, cy) || !inB(cx + 1, cy) || !inB(cx, cy - 1) || !inB(cx, cy + 1);
      at(x, y, rim ? SG.white : ((x + y) % 3 === 0 ? '#6a80a0' : SG.stripe));
    } else if (d <= 0.85) at(x, y, SG.white);
  }
  // OLD TOWN: red neon letters on the dark panel that bridges the gap in the outline
  const px0 = Math.round(19 * K), px1 = Math.round(66 * K), py0 = Math.round(78 * K), py1 = SH - 1;
  for (let y = py0; y <= py1; y++) for (let x = px0; x <= px1; x++) {
    const end = (x === px0 || x === px1) && (y === py0 || y === py1);
    if (!end) at(x, y, y === py0 ? '#4a1c22' : SG.panel);
  }
  const word = 'OLD TOWN', wdt = [...word].reduce((w, ch) => w + (ch === ' ' ? 3 : OLDTOWN_FONT[ch][0].length + 1), -1);
  const ly = py0 + Math.round((py1 - py0 - 6) / 2);
  let cx = Math.round((px0 + px1 - wdt) / 2) + 1;
  for (const ch of word) {
    if (ch === ' ') { cx += 3; continue; }
    const g = OLDTOWN_FONT[ch];
    g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') at(cx + i, ly + j, j === 0 ? SG.redH : (row[i - 1] !== '#' ? SG.redH : SG.red)); });
    cx += g[0].length + 1;
  }
  return p.done();
}
// the glow round every lit tube and bulb: their colours spread over a few pixels, dithered, drawn with 'lighter'
function renderSignGlow(signC) {
  const sx = signC.getContext('2d'), src = sx.getImageData(0, 0, W, H).data, R = 5;
  const x0 = SIGN.x - 8, x1 = SIGN.x + SIGN.w + 8, y0 = SIGN.y - 8, y1 = SIGN.y + SIGN.h + 2, gw = x1 - x0, gh = y1 - y0;
  const acc = new Float32Array(gw * gh * 3);
  for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
    const o = (y * W + x) * 4;
    if (!src[o + 3]) continue;
    const r = src[o], g = src[o + 1], b = src[o + 2];
    if (r + g + b < 300) continue;                                   // only what is lit: tubes and bulbs
    for (let j = -R; j <= R; j++) for (let i = -R; i <= R; i++) {
      const d = Math.hypot(i, j); if (d > R) continue;
      const gx = x + i - x0, gy = y + j - y0; if (gx < 0 || gy < 0 || gx >= gw || gy >= gh) continue;
      const f = (1 - d / R) ** 2 * 0.06, q = (gy * gw + gx) * 3;
      acc[q] += r * f; acc[q + 1] += g * f; acc[q + 2] += b * f;
    }
  }
  const p = painter(gw, gh);
  for (let y = 0; y < gh; y++) for (let x = 0; x < gw; x++) {
    const q = (y * gw + x) * 3, m = Math.max(acc[q], acc[q + 1], acc[q + 2]);
    if (m < 4) continue;
    const a = Math.min(1, m / 255), lv = Math.floor(a * 6 + bayer(x, y)) / 6;
    if (lv <= 0) continue;
    const s = 255 / m, col = '#' + [acc[q], acc[q + 1], acc[q + 2]].map(c => Math.round(c * s).toString(16).padStart(2, '0')).join('');
    p.put(x, y, col, Math.round(Math.min(1, lv) * 0.55 * 255));
  }
  return { c: p.done(), x: x0, y: y0 };
}

/* ================= The White Stag Building and Waterfront Park ================= */
// the brick block under the sign: its top floor's big steel-sash windows show over the park's trees
function renderWhiteStag() {
  const p = painter(W, H), put = p.put, x0 = SIGN.x - 4, x1 = SIGN.x + SIGN.w + 4;
  for (let y = ROOF; y < 150; y++) for (let x = x0; x < x1; x++) {
    let col = x === x0 ? '#a8664c' : y === ROOF ? '#c8a088' : y === ROOF + 1 ? '#4a2a24' : (x > x1 - 4 ? '#5a3028' : '#7e4434');
    const wy = (y - ROOF - 4) % 8, wx = (x - x0 - 3) % 10;
    if (y > ROOF + 3 && wy >= 0 && wy < 5 && wx >= 0 && wx < 7 && x < x1 - 3) {
      const lit = hash(x0, Math.floor((x - x0 - 3) / 10), Math.floor((y - ROOF - 4) / 8)) < 0.5;
      col = (wx === 3 || wy === 2) ? '#2a2026' : lit ? (wy < 2 ? '#ffe0a0' : '#f0b062') : '#24263a';
    }
    put(x, y, col);
  }
  return p.done();
}
// the park's trees along the seawall, their crowns lit from below by the globe lamps; the promenade; the seawall
const PARK_LAMPS = [];
for (let x = 92; x < W; x += 16) PARK_LAMPS.push(x);
function renderPark() {
  const p = painter(W, H), put = p.put, r = rng(23);
  // the far bank's crowns: elms north of the bridge, the market's trees seen under it
  for (let k = 0; k < 42; k++) {
    const cx = 84 + k * 9.5 + r() * 5, rx = 5 + r() * 4, ry = 4 + r() * 3, cy = 142 - ry * 0.6 + r() * 2;
    if (cx > SIGN.x + 2 && cx < SIGN.x + SIGN.w - 2 && cy - ry < ROOF + 6) continue;
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let x = Math.floor(cx - rx); x <= cx + rx; x++) {
      const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
      if (d > 1 || (d > 0.75 && bayer(x, y) < 0.5)) continue;
      const lit = (y - cy) / ry, n = hash(x >> 1, y >> 1, 9);
      put(x, y, lit > 0.45 ? (n < 0.5 ? '#3a4a34' : '#2e3c2e') : n < 0.35 ? '#1e2a2a' : '#18222a');
    }
  }
  // the promenade behind the seawall, then the wall itself, its cap catching the lamps
  for (let x = 60; x < W; x++) {
    for (let y = 147; y < 150; y++) put(x, y, y === 147 ? '#3a3a48' : '#2a2a36');
    for (let y = 150; y < WL; y++) put(x, y, y === 150 ? '#8a8478' : y === 151 ? '#5a5650' : (x % 23 === 0 ? '#2e2e34' : '#3e3c40'));
  }
  // the globe lamps on their posts
  for (const x of PARK_LAMPS) { for (let y = 143; y < 150; y++) put(x, y, '#2a2a32'); put(x, 142, '#fff0c8'); put(x - 1, 142, '#f2c880'); put(x + 1, 142, '#e8b060'); put(x, 141, '#f8dca0'); }
  return p.done();
}

/* ================= The Willamette ================= */
function renderWater() {
  return ditherFill(W, KERB - WL, ['#0a1418', '#0c1a1e', '#0f2024', '#13282a', '#183030'], (x, y) => 0.25 + 0.55 * (y / (KERB - WL)) + 0.15 * Math.max(0, 1 - Math.abs(x - 280) / 160));
}

/* ================= The Esplanade's floating walkway: rail, piles, lamp, bench ================= */
const PILES = [{ x: 186, top: 150 }, { x: 362, top: 152 }];
const BENCH = { x0: 196, x1: 230, y: 200 };
function renderWalkway() {
  const p = painter(W, H), put = p.put;
  // the walkway's edge on the water and its concrete deck, wet, in slabs
  for (let x = 0; x < W; x++) {
    put(x, KERB, '#6a6c78'); put(x, KERB + 1, '#3a3c48'); put(x, KERB + 2, '#22242e');
    for (let y = KERB + 3; y < H; y++) {
      const v = (y - KERB) / 70 + (bayer(x, y) - 0.5) * 0.18;
      let col = v < 0.3 ? '#2a2c3a' : v < 0.65 ? '#242632' : '#1e202a';
      if ((y - KERB - 3) % 22 === 0 || (x + (y > 230 ? 30 : 0)) % 60 === 0) col = '#181a24';
      put(x, y, col);
    }
  }
  // the rail: posts and two rails, thin steel
  for (let x = 0; x < W; x++) { put(x, 181, '#7a7e8c'); put(x, 182, '#3a3c48'); put(x, 188, '#4a4c5a'); }
  for (let x = 6; x < W; x += 24) for (let y = 181; y <= KERB; y++) { put(x, y, '#5a5e6c'); put(x + 1, y, '#2a2c38'); }
  // the piles that hold the floating walkway: rust-red steel, white caps
  for (const P of PILES) {
    for (let y = P.top; y <= KERB + 1; y++) for (let x = P.x - 2; x <= P.x + 2; x++) put(x, y, x === P.x - 2 ? '#c06a48' : x === P.x + 2 ? '#5a2a20' : x < P.x + 1 ? '#9a4a34' : '#7a3a2a');
    for (let k = 0; k < 4; k++) for (let x = P.x - 2 + (k >> 1); x <= P.x + 2 - (k >> 1); x++) put(x, P.top - 1 - k, x <= P.x ? '#f0ece4' : '#b8b4ac');
  }
  // the Esplanade's lamp post, near the viewer on the left
  for (let y = LAMP.y; y < 210; y++) { put(LAMP.x, y, '#4a4e5a'); put(LAMP.x + 1, y, '#22242e'); }
  for (let x = LAMP.x - 3; x <= LAMP.x + 4; x++) put(x, LAMP.y - 1, '#3a3e4a');
  for (let x = LAMP.x - 2; x <= LAMP.x + 3; x++) put(x, LAMP.y, '#fff2cc');
  for (let x = LAMP.x - 3; x <= LAMP.x + 5; x++) put(x, 210, '#2a2c36');
  // the bench, its back to the viewer, facing the river
  const { x0, x1, y } = BENCH;
  for (let x = x0; x <= x1; x++) { put(x, y - 9, '#7a5a40'); put(x, y - 8, '#5a4030'); put(x, y - 6, '#7a5a40'); put(x, y - 5, '#4a3426'); put(x, y - 3, '#3a2a20'); }
  for (const lx of [x0 + 2, x1 - 2]) for (let k = y - 10; k <= y + 1; k++) put(lx, k, '#2a2a32');
  return p.done();
}

// fallen bigleaf-maple leaves on the wet walkway, yellow and rust, more along the rail
function renderLeaves() {
  const p = painter(W, H), r = rng(91);
  for (let k = 0; k < 46; k++) {
    const x = Math.floor(r() * W), y = FEET + 3 + Math.floor(Math.pow(r(), 1.6) * 60);
    if (x > CART.x0 - 4 && x < CART.x1 + 4 && y < CART.base + 4) continue;
    const col = ['#c89a2a', '#b8742a', '#8a5a2a', '#d8b040'][Math.floor(r() * 4)];
    p.put(x, y, col); p.put(x + 1, y, mixHex(col, '#1e202a', 0.3)); if (r() < 0.5) p.put(x, y - 1, mixHex(col, '#1e202a', 0.4));
  }
  return p.done();
}

/* ================= The food cart on the right: the cozy corner ================= */
// a boxy trailer painted teal, its hatch propped open on a lit kitchen, an awning, a hand-painted menu, the name on
// the roof, string lights to a pole, a picnic table
const CART_SIGN = 'DUMPLINGS';
function renderCart() {
  const p = painter(W, H), put = p.put, { x0, x1, top, base } = CART, b0 = top + 8, b1 = base - 8;
  for (let y = b0; y <= b1; y++) for (let x = x0; x <= x1; x++) {
    const v = (x - x0) / (x1 - x0) * 0.6 + (bayer(x, y) - 0.5) * 0.14;
    put(x, y, y === b0 ? '#9ae0d0' : y === b1 ? '#1e3a38' : x === x0 ? '#8ad0c0' : v < 0.25 ? '#5ab4a4' : v < 0.5 ? '#46988a' : '#367a70');
  }
  for (let x = x0 + 1; x < x1; x++) put(x, b1 - 4, '#e8e0c8');                    // a cream stripe along the skirt
  // the hatch: the lit kitchen behind, steam, a shelf with jars; the awning propped over it
  const hx0 = x0 + 10, hx1 = x1 - 14, hy0 = b0 + 8, hy1 = b0 + 22;
  for (let y = hy0; y <= hy1; y++) for (let x = hx0; x <= hx1; x++) {
    const v = (y - hy0) / (hy1 - hy0) + (bayer(x, y) - 0.5) * 0.2;
    put(x, y, v < 0.3 ? '#ffe8b0' : v < 0.65 ? '#f6c070' : '#d8904a');
  }
  for (let x = hx0; x <= hx1; x += 5) { put(x, hy0 + 3, '#8a5a3a'); put(x + 1, hy0 + 3, '#c88a4a'); }
  for (let x = hx0 - 1; x <= hx1 + 1; x++) { put(x, hy1 + 1, '#c8c0b0'); put(x, hy1 + 2, '#6a6458'); }  // the counter
  for (let k = 0; k < 6; k++) for (let x = hx0 - 2 - k; x <= hx1 + 2 + k; x++) put(x, hy0 - 1 - k, k === 5 ? '#5a2a2a' : ((x >> 2) & 1) ? '#e8dcc8' : '#c84a3e');
  // the menu board beside the hatch, chalk lines
  for (let y = hy0; y <= hy1 + 2; y++) for (let x = x1 - 11; x <= x1 - 3; x++) put(x, y, x === x1 - 11 || x === x1 - 3 || y === hy0 || y === hy1 + 2 ? '#6a4a30' : '#1e2420');
  for (const [k, w] of [[3, 5], [6, 4], [9, 6], [12, 3], [15, 5]]) for (let x = x1 - 9; x < x1 - 9 + w; x++) put(x, hy0 + k, '#c8ccc0');
  // the roof, the name board on top, the wheels and the hitch
  for (let x = x0 - 1; x <= x1 + 1; x++) { put(x, b0 - 1, '#c8c4bc'); put(x, b0 - 2, '#8a8680'); }
  for (let y = top; y < b0 - 2; y++) for (let x = x0 + 8; x <= x1 - 8; x++) put(x, y, y === top || y === b0 - 3 || x === x0 + 8 || x === x1 - 8 ? '#2a1a1a' : '#4a2420');
  for (const wx of [x0 + 12, x1 - 12]) for (let j = -3; j <= 3; j++) for (let i = -3; i <= 3; i++) {
    const d = Math.hypot(i, j); if (d <= 3.2) put(wx + i, b1 + 4 + j, d > 2.2 ? '#14141a' : d > 1 ? '#3a3a44' : '#8a8a94');
  }
  for (let x = x0 - 8; x < x0; x++) put(x, b1 + 1, '#3a3a44');
  put(x0 - 9, b1 + 2, '#3a3a44'); put(x0 - 9, b1 + 3, '#3a3a44');
  // the pole for the string lights and the picnic table
  for (let y = top - 14; y < base; y++) { put(472, y, '#3a3c48'); put(473, y, '#1e2028'); }
  for (let x = 456; x <= 478; x++) { put(x, 194, '#8a6a48'); put(x, 195, '#5a4430'); }
  for (const lx of [459, 475]) for (let y = 196; y < 205; y++) put(lx, y, '#4a3a2a');
  for (let x = 454; x <= 480; x++) put(x, 200, '#6a5038');
  const c = p.done();
  sign(p.x, CART_SIGN, Math.round((x0 + x1 - signWidth(CART_SIGN)) / 2), top + 1, '#ffd890');
  return c;
}

/* ================= The rooftop billboard in Old Town ================= */
// a steel frame on the roof of a brick block, a catwalk, three gooseneck lamps; the city's own ad is a made-up
// doughnut shop: DRIZZLE DONUTS, a pink-glazed ring with sprinkles
const BB_LAMPS = [0.2, 0.5, 0.8].map(f => BB.x + Math.round((BB.w - 1) * f));
function renderBillboard() {
  const p = painter(W, H), put = p.put, X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const [cx, roof] of [[BB.x + 6, 120], [BB.x + BB.w - 7, 118]]) for (let y = Y1 + 1; y < roof; y++) { put(cx, y, '#3a3a48'); put(cx + 1, y, '#16161e'); }   // legs down to each roof
  bres(put, BB.x + 6, Y1 + 3, BB.x + BB.w - 7, 117, '#24242e'); bres(put, BB.x + BB.w - 7, Y1 + 3, BB.x + 6, 119, '#24242e');
  for (let x = X0 - 2; x <= X1 + 2; x++) { put(x, Y1 + 1, '#4a4a5a'); put(x, Y1 + 2, (x & 1) ? '#22222c' : '#16161e'); }
  for (let x = X0; x <= X1; x++) { put(x, Y0, '#14141c'); put(x, Y1, '#14141c'); put(x, Y0 + 1, '#2e3040'); put(x, Y1 - 1, '#5a4a40'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#14141c'); put(X1, y, '#14141c'); put(X0 + 1, y, '#2a2c3c'); put(X1 - 1, y, '#16161e'); }
  for (const lx of BB_LAMPS) {
    for (let y = Y0 - 4; y < Y0; y++) put(lx, y, '#3a3a48');
    for (let i = lx - 2; i <= lx + 2; i++) put(i, Y0 - 5, i === lx - 2 ? '#4a4a5a' : '#2a2a36');
  }
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + x - lx))) / 9;
    put(x, y, ramp(['#e8d6bc', '#d8c0a0', '#c4a682', '#a88a6a'], clamp(y / h * 0.7 + lamp * 0.25, 0, 1), x, y));
  }
  for (let x = 1; x < w - 1; x++) { put(x, 1, '#3a6a5a'); put(x, h - 2, '#2a5246'); }
  for (let y = 1; y < h - 1; y++) { put(1, y, '#3a6a5a'); put(w - 2, y, '#2a5246'); }
  // the doughnut: a ring of dough under pink glaze dripping over its edge, sprinkles on top
  const cx = 9, cy = 10;
  for (let y = -6; y <= 6; y++) for (let x = -6; x <= 6; x++) {
    const d = Math.hypot(x, y * 1.1);
    if (d > 6 || d < 2.2) continue;
    let col = d > 5.2 || y > 3 ? '#b8743a' : '#e86a9a';
    if (col === '#e86a9a' && x < -1 && y < -1) col = '#ff9ec0';
    if (y === 3 && (x === -3 || x === 1 || x === 4)) col = '#e86a9a';
    put(cx + x, cy + y, col);
  }
  for (const [x, y, c] of [[-3, -3, '#f8f0c8'], [2, -4, '#5ac8e8'], [4, -1, '#f8e060'], [-4, 1, '#5ac8e8'], [0, -5, '#f8e060'], [3, 2, '#f8f0c8']]) put(cx + x, cy + y, c);
  const c = p.done();
  sign(p.x, 'DRIZZLE', 19, 4, '#2a4a40');
  sign(p.x, 'DONUTS', 19, 11, '#c83a6a');
  return gradeAd(c, { level: 0.7, tint: '#141a3a', mix: 0.18, wear: 0.15, seed: 61 });
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.7);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 7, 3, 1, '#f2dcaa'); halo(HL.bbLamp, lx, BB.y - 6, 0.85); }
}

/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2c8a0', '#e0aa80', '#c08a60', '#8a5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#c8a058', '#8e3e1c', '#c8c4c0'];
const PNIGHT = '#141a3a', GLOW = '#ffd8a0';
// a look for a person h px tall: proportions and colours dimmed for the night, with a warm line of light
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * 0.37);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 18 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOW, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, GLOW, 0.32), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, apron: o.apron ? m(o.apron) : null, scarf: o.scarf ? m(o.scarf) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 phone up, 3 out (pouring, offering), 5 hand to the face
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);
  if (P.apron && view !== 0) R(side ? (dir > 0 ? x : x - 1) : tx + 1, ty + 2, side ? 2 : bw - 2, th + 2, P.apron);
  if (P.scarf) R(tx, ty, bw, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ad8e8'); } }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 2, 1, ty - hy + 3, P.topD); R(ax, hy - 3, 1, 1, P.skin); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
// an umbrella over someone whose head is at row hy: a dome of four rows and its shaft
function drawUmbrella(x, hy, col, w = 13) {
  const hw = w >> 1, lt = mixHex(col, '#ffffff', 0.18), dk = mixHex(col, '#000000', 0.3);
  for (let k = 0; k < 4; k++) R(x - hw + 3 - k, hy - 7 + k, w - 2 * (3 - k), 1, k === 0 ? lt : k === 3 ? dk : col);
  R(x, hy - 8, 1, 1, '#2a2a32'); R(x, hy - 3, 1, 4, '#2a2a32');
}
// a cyclist in profile (dir 1 rides right): wheels, a diamond frame, the rider leaning to the bars, one leg down on
// its pedal and one up, a white lamp in front and a red one behind; ph turns the cranks
function drawCyclist(x, fy, dir, P, ph = 0) {
  const X = i => Math.round(x + dir * i), wr = 4, wy = fy - wr, frame = '#c8a040';
  for (const cx of [X(-7), X(7)]) for (let j = -wr; j <= wr; j++) for (let i = -wr; i <= wr; i++) {
    const d = Math.hypot(i, j); if (d <= wr + 0.35 && d >= wr - 0.65) R(cx + i, wy + j, 1, 1, '#14161e');
  }
  const line = (x0, y0, x1, y1, c) => bres((px, py) => R(px, py, 1, 1, c), x0, y0, x1, y1);
  line(X(-7), wy, X(-1), fy - 11, frame); line(X(-1), fy - 11, X(0), wy, frame); line(X(0), wy, X(-7), wy, frame);
  line(X(0), wy, X(5), fy - 11, frame); line(X(-1), fy - 11, X(5), fy - 11, frame); line(X(5), fy - 11, X(7), wy, '#8a8a94');
  R(X(4), fy - 13, 1, 2, '#3a3a44'); R(dir > 0 ? X(4) : X(5), fy - 13, 2, 1, '#3a3a44');
  const a = TAU * ph, k1 = [X(Math.round(2.4 * Math.cos(a))), wy + Math.round(2.4 * Math.sin(a))], k2 = [X(-Math.round(2.4 * Math.cos(a))), wy - Math.round(2.4 * Math.sin(a))];
  const hip = [X(-1), fy - 13], sh = [X(2), fy - 19];
  line(hip[0], hip[1], k2[0], k2[1] - 4, mixHex(P.bot, '#000000', 0.25)); line(k2[0], k2[1] - 4, k2[0], k2[1], mixHex(P.bot, '#000000', 0.25));
  line(hip[0], hip[1], k1[0], k1[1] - 4, P.bot); line(k1[0], k1[1] - 4, k1[0], k1[1], P.bot); R(k1[0], k1[1], 1, 1, P.shoe);
  for (let k = 0; k <= 6; k++) { const px = Math.round(lerp(hip[0], sh[0], k / 6)), py = Math.round(lerp(hip[1], sh[1], k / 6)); R(px - 1, py, 3, 1, k === 6 ? P.topL : P.top); }
  line(sh[0], sh[1] + 1, X(4), fy - 13, P.topD);
  R(sh[0] - 1, sh[1] - 4, 4, 4, P.skin); R(sh[0] - 1, sh[1] - 5, 4, 2, P.hat ?? P.hair);
  R(X(8), wy - 2, 1, 1, '#fff4c8'); R(X(-8), wy - 2, 1, 1, '#ff3a2a');
  return [X(8), wy - 2];
}
// a dog trotting in profile, nose towards dir
function drawDog(x, fy, dir, step) {
  const c = '#6a4a34', d = '#3e2a20';
  R(x - 4, fy - 5, 8, 3, c); R(x + dir * 4, fy - 7, 3, 3, c); R(x + dir * 6, fy - 6, 1, 1, d);
  R(x - dir * 5, fy - 6, 1, 2, c);
  for (const [lx, up] of [[-3, step], [2, 1 - step]]) R(x + lx, fy - 2, 1, 2 - up, d);
}

/* ================= Portland's own drizzle at three depths, rings on the river, splashes on the walkway ================= */
const DRIZZLE = [
  { n: 150, nMin: 70, nMax: 92, len: 1, col: '#7c84b0', lit: '#c8a878', a: 0.32, land: [70, WL], slope: 0.12 },
  { n: 90, nMin: 100, nMax: 124, len: 2, col: '#969cc4', lit: '#e8c890', a: 0.38, land: [WL, KERB], slope: 0.16 },
  { n: 44, nMin: 150, nMax: 176, len: 4, col: '#b4b8dc', lit: '#ffe0b0', a: 0.46, land: [KERB, H + 6], slope: 0.22 },
].map((L, d) => ({ ...L, drops: Array.from({ length: L.n }, (_, i) => ({ n: L.nMin + Math.floor(hash(i, d, 71) * (L.nMax - L.nMin)), ph: hash(i, d, 72), s: i * 7 + d })) }));
const RAIN_LIGHTS = [{ x: LAMP.x, y: LAMP.y + 4, r: 44 }, { x: (CART.x0 + CART.x1) >> 1, y: 180, r: 34 }, { x: SU(42), y: SV(50), r: 50 }];
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y - 4, r: 16 }];
const litAt = (x, y, lights) => lights.some(l => (x - l.x) ** 2 + (y - l.y) ** 2 < l.r * l.r);
function drawDrizzle(depth, t, lights, k = 1) {
  const L = DRIZZLE[depth], n = Math.round(L.drops.length * k);
  ctx.globalAlpha = L.a;
  for (let i = 0; i < n; i++) {
    const d = L.drops[i];
    const { cyc, u } = cycle(t, d.n, d.ph), land = lerp(L.land[0], L.land[1], hash(d.s, cyc, 73));
    const x0 = hash(d.s, cyc, 74) * (W + 40) - 20, y = Math.round(lerp(-8, land, u)), x = Math.round(x0 + (y - land) * L.slope);
    ctx.fillStyle = litAt(x, y, lights) ? L.lit : L.col;
    ctx.fillRect(x, y, 1, L.len);
  }
  ctx.globalAlpha = 1;
}
const RINGS = Array.from({ length: 30 }, (_, i) => ({ n: 150 + Math.floor(hash(i, 81) * 40), ph: hash(i, 82), s: i }));
function drawRings(t, k = 1) {
  ctx.fillStyle = '#7a9aa4';
  for (let i = 0; i < Math.round(RINGS.length * k); i++) {
    const r = RINGS[i];
    const { cyc, u } = cycle(t, r.n, r.ph);
    if (u > 0.3) continue;
    const x = Math.round(hash(r.s, cyc, 83) * W), y = Math.round(lerp(WL + 6, KERB - 3, hash(r.s, cyc, 84))), w = 1 + Math.round(u * 9);
    ctx.globalAlpha = 0.45 * (1 - u / 0.3);
    ctx.fillRect(x - w, y, 1, 1); ctx.fillRect(x + w, y, 1, 1);
    if (w > 2) { ctx.fillRect(x - w + 1, y - 1, 2 * w - 1, 1); ctx.fillRect(x - w + 1, y + 1, 2 * w - 1, 1); }
  }
  ctx.globalAlpha = 1;
}
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[90, WL], [WL + 2, KERB], [KERB + 6, H + 4]];
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })), leaves: { wind: [0, 0, W, 200] } };
const WX_BB = { ...WX, lights: RAIN_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 3.2 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let WISP_C, MIST_C, LEAF_C, LEAVES_C, HL, SKY_C, MOON_C, HILLS_C, DOWN_C, TOWN_C, BRIDGE_C, STAG_C, SIGN_C, SIGN_GLOW, PARK_C, WATER_C, WALK_C, CART_C, BB_C, BB_AD, PITCH, VIG_C;
function prerender() {
  HL = {
    moon: makeHalo(40, '#9ab0ff', 0.2, 1.8), moonCore: makeHalo(14, '#fff2d0', 0.3, 1.5),
    haze: makeHalo(150, '#ff9a4a', 0.06, 1.3), red: makeHalo(4, '#ff3a2a', 0.55, 1.2), koin: makeHalo(14, '#7ea0e8', 0.18, 1.5),
    sign: makeHalo(56, '#ffb050', 0.1, 1.6), signGreen: makeHalo(40, '#3cf070', 0.06, 1.6), stag: makeHalo(18, '#d8ecff', 0.12, 1.5),
    old: makeHalo(18, '#ff4a34', 0.14, 1.5), tower: makeHalo(8, '#ffd090', 0.3, 1.4), brLamp: makeHalo(7, '#ffe8b8', 0.35, 1.4),
    park: makeHalo(9, '#ffd890', 0.3, 1.4), win: makeHalo(8, '#ffb45a', 0.18),
    lamp: makeHalo(56, '#ffb060', 0.2, 1.7), lampCore: makeHalo(14, '#ffe6b0', 0.5, 1.4),
    hatch: makeHalo(34, '#ffa850', 0.26, 1.5), pool: makeHalo(30, '#ffb060', 0.12, 1.4), bike: makeHalo(7, '#fff4c8', 0.4, 1.3), phone: makeHalo(4, '#8ad8e8', 0.35, 1.3), mast: makeHalo(5, '#ffffff', 0.4, 1.3), bulb: makeHalo(4, '#ffd890', 0.45, 1.2), sheen: makeHalo(60, '#d89a48', 0.06, 1.4),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.6), bbWash: makeHalo(36, '#ffb870', 0.06, 1.5), union: makeHalo(10, '#ff5a3a', 0.22, 1.4),
  };
  WISP_C = WISPS.map(renderWisp); MIST_C = renderHillMist();
  SKY_C = renderSky(); MOON_C = renderMoon(); HILLS_C = renderHills(); DOWN_C = renderDowntown(); TOWN_C = renderOldTown();
  BRIDGE_C = renderBridge();
  { const p = painter(W, H); leafPixels(p.put, 0, 0); leafPixels(p.put, 1, 0); LEAF_C = p.done(); } STAG_C = renderWhiteStag(); SIGN_C = renderSign(); SIGN_GLOW = renderSignGlow(SIGN_C);
  PARK_C = renderPark(); WATER_C = renderWater(); WALK_C = renderWalkway(); CART_C = renderCart();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#3f7d3a' });
  VIG_C = renderVignette(); LEAVES_C = renderLeaves();
}

/* ================= The loop's timeline (seconds) =================
   all loop   drizzle breathes (heavier, lighter, three times a loop); the bulbs shimmer; the masts' and Big Pink's
              red lamps; Old Town's windows; cars and buses over the bridge; cyclists, a jogger and walkers along
              the Esplanade; the busker strums; the food cart serves someone every 12 s
   16-24      a great blue heron (the city's bird) glides in over the river and lands on the left pile; leaves at 96
   34-112     THE LIFT: a sailboat motors up along the far bank from the right (34), signals the bridge with its horn
              (52); the bells ring, the gates' red lamps blink, the traffic queues (54); both leaves rise (58-70); the
              boat slips through the open span (80-90) and away behind the bridge; the leaves come down (92-104); the
              queued cars move off (108)
   126-150    a sculler with a white light rows across the near river, right to left
   150-206    a dinner-cruise boat, decks of warm windows, glides downriver along the far bank; horn at 156
   166-174    the heron flies in again to the right pile; leaves at 222
   212-236    the sculler rows back, left to right                                                                  */
const LIFT = { horn: 52, bell: 54, up0: 58, up1: 70, down0: 92, down1: 104, go: 108 };
const SAIL = [[34, 500, 161, 0.8], [70, 236, 161, 0.8], [84, 116, 163, 1.27], [92, 92, 161, 1.0]];   // t, x, waterline, scale
const CRUISE = { t0: 150, t1: 206, x0: 206, x1: 520 };
const HERON = [{ in: 16, land: 24, off: 96, gone: 104, pile: 0, from: 1 }, { in: 166, land: 174, off: 222, gone: 230, pile: 1, from: -1 }];
const SCULL = [{ t0: 126, t1: 150, dir: -1 }, { t0: 212, t1: 236, dir: 1 }];
const liftAngle = t => 1.2 * (smooth(LIFT.up0, LIFT.up1, t) - smooth(LIFT.down0, LIFT.down1, t));
const liftOn = t => t >= LIFT.bell && t < LIFT.go;
// drizzle breathes: 0.32 at its lightest, 1 at its heaviest, three times a loop
const drizzleAt = t => 0.66 + 0.34 * wave(t, 3, 0.15);

/* ================= Traffic over the Burnside Bridge ================= */
// cars and buses on the deck, their roofs and lamps over the balustrade: westbound away from us (left to right,
// shrinking), eastbound towards us. Regular traffic skips the lift; four cars queue at the gates and go on at 108.
const CARS = [];
{
  const r = rng(29), cols = ['#c8c8d0', '#3a4a6a', '#8a2a2a', '#2a2a30', '#d8c890', '#4a6a5a', '#e8e8ec'];
  for (let k = 0; k < 16; k++) {
    const dir = k % 2 ? -1 : 1, t0 = (k * 15 + r() * 8) % LOOP, bus = k === 3 || k === 10;
    if (t0 > LIFT.bell - 12 && t0 < LIFT.go + 2) continue;
    CARS.push({ t0, dur: bus ? 16 : 11, dir, bus, col: cols[k % cols.length] });
  }
}
const QUEUE = [{ dir: 1, x: SPAN.x0 - 7, at: LIFT.bell - 4, col: '#d8c890' }, { dir: 1, x: SPAN.x0 - 15, at: LIFT.bell - 1, col: '#3a4a6a' },
  { dir: -1, x: SPAN.x1 + 8, at: LIFT.bell - 3, col: '#c8c8d0' }, { dir: -1, x: SPAN.x1 + 15, at: LIFT.bell, col: '#8a2a2a', bus: true }];
function drawVehicle(x, dir, col, bus, braking) {
  const y = deckY(x), t = deckT(x), sc = t / 8, bal = t >= 6 ? 2 : 1;
  const w = Math.max(3, Math.round((bus ? 12 : 5) * sc)), h = bus ? Math.max(3, Math.round(4 * sc)) : Math.max(2, Math.round(2.4 * sc));
  const x0 = Math.round(x - w / 2), top = Math.round(y - bal - h);
  R(x0, top, w, h + bal, col);
  R(x0 + 1, top + 1, w - 2, 1, bus ? '#ffe2a8' : '#2a3448');
  R(dir > 0 ? x0 + w - 1 : x0, top + h - 1, 1, 1, '#fff4d0');                       // headlamp
  R(dir > 0 ? x0 : x0 + w - 1, top + h - 1, 1, 1, braking ? '#ff3a2a' : '#a82a22'); // tail lamp
  if (braking) halo(HL.red, dir > 0 ? x0 : x0 + w - 1, top + h - 1, 0.6);
}
function drawTraffic(t) {
  const xs = [BR.x0 - 6, BR.x1 + 2];
  for (const c of CARS) {
    const u = ((t - c.t0) % LOOP + LOOP) % LOOP / c.dur;
    if (u >= 1) continue;
    const x = c.dir > 0 ? lerp(xs[0], xs[1], u) : lerp(xs[1], xs[0], u);
    drawVehicle(x, c.dir, c.col, c.bus, false);
  }
  for (const q of QUEUE) {                                       // drive up, wait at the gate, then go on
    if (t < q.at - 4 || t > LIFT.go + 14) continue;
    const from = q.dir > 0 ? BR.x0 - 6 : BR.x1 + 2, to = q.dir > 0 ? BR.x1 + 2 : BR.x0 - 6;
    const x = t < q.at ? lerp(from, q.x, smooth(q.at - 4, q.at, t)) : t < LIFT.go ? q.x : lerp(q.x, to, smooth(LIFT.go, LIFT.go + 14, t) ** 1.4);
    drawVehicle(x, q.dir, q.col, q.bus, t >= q.at - 1 && t < LIFT.go + 1);
  }
}
// the gates' red lamps blink in turn while the bridge is closed to traffic
function drawGates(t) {
  for (const [gx, k] of [[SPAN.x0 - 3, 0], [SPAN.x1 + 3, 1]]) {
    const y = Math.round(deckY(gx)) - (gx < 100 ? 7 : 5);
    R(gx, y, 1, gx < 100 ? 5 : 3, '#2a2a34');
    if (!liftOn(t)) continue;
    const on = (step(t, 480) + k) % 2 === 0;
    R(gx, y - 1, 1, 1, on ? '#ff3a2a' : '#5a1a1a');
    if (on) halo(HL.red, gx, y - 1, 0.9);
  }
}

/* ================= Boats on the Willamette ================= */
// the sailboat the bridge opens for: white hull, a cabin, its mast and furled sail, masthead and side lights;
// x is the bow's centre and wl the waterline; it heads left (upriver)
function drawSailboat(x, wl, s, a = 1) {
  const L = Math.round(16 * s), hh = Math.max(2, Math.round(3 * s)), mh = Math.round(35 * s), x0 = Math.round(x), y = Math.round(wl);
  ctx.globalAlpha = a;
  for (let k = 0; k < hh; k++) R(x0 + (k < 1 ? 1 : 0) + k, y - hh + k, L - k * 2, 1, k === 0 ? '#f0f0f4' : '#b8bcc8');
  R(x0 + Math.round(L * 0.45), y - hh - Math.max(2, Math.round(2 * s)), Math.round(L * 0.35), Math.max(2, Math.round(2 * s)), '#d8dce4');
  R(x0 + Math.round(L * 0.5), y - hh - Math.max(2, Math.round(2 * s)) + 1, 2, 1, '#ffd890');
  const mx = x0 + Math.round(L * 0.4);
  R(mx, y - hh - mh, 1, mh, '#a8acb8');
  R(mx, y - hh - Math.round(5 * s), Math.round(L * 0.5), 1, '#e8e8ec');                 // boom and furled sail
  R(mx, y - hh - mh - 1, 1, 1, '#ffffff'); halo(HL.mast, mx, y - hh - mh - 1, 0.8 * a);
  R(x0, y - hh, 1, 1, '#ff3a3a'); R(x0 + L - 1, y - hh, 1, 1, '#ffffff');
  ctx.globalAlpha = a * 0.3;                                                              // its mirror in the river
  R(x0 + 1, y + 1, L - 2, 1, '#8a90a0'); R(mx, y + 2, 1, Math.round(mh * 0.4), '#7a8090');
  ctx.globalAlpha = 1;
}
function sailAt(t) {
  if (t < SAIL[0][0] || t >= SAIL[SAIL.length - 1][0]) return null;
  let k = 1; while (t >= SAIL[k][0]) k++;
  const [ta, xa, wa, sa] = SAIL[k - 1], [tb, xb, wb, sb] = SAIL[k], u = (t - ta) / (tb - ta);
  return { x: lerp(xa, xb, u), wl: lerp(wa, wb, u), s: lerp(sa, sb, u), behind: t > 86, a: t > 86 ? 1 - smooth(86, 92, t) : 1 };
}
// the dinner-cruise boat: a long white hull, two decks of warm windows, the open top deck with its string of lights
function drawCruise(t) {
  if (t < CRUISE.t0 || t >= CRUISE.t1) return null;
  const u = (t - CRUISE.t0) / (CRUISE.t1 - CRUISE.t0), x = Math.round(lerp(CRUISE.x0, CRUISE.x1, u)), wl = 160, L = 46;
  ctx.globalAlpha = smooth(CRUISE.t0, CRUISE.t0 + 3, t);
  R(x - L, wl - 4, L, 4, '#e8e8ec'); R(x - L + 1, wl - 1, L - 2, 1, '#8a8e9a'); R(x - 2, wl - 4, 3, 2, '#e8e8ec');
  for (const [dy, w0, w1] of [[8, 4, 6], [13, 8, 12]]) {
    R(x - L + w0, wl - dy - 1, L - w0 - w1, 5, '#d8dce4');
    for (let i = x - L + w0 + 2; i < x - w1 - 1; i += 3) R(i, wl - dy, 2, 2, hash(i, dy, 5) < 0.85 ? '#ffd890' : '#f2b060');
  }
  for (let i = x - L + 10; i < x - 14; i += 3) R(i, wl - 15 + ((i >> 2) & 1), 1, 1, '#ffe8b0');
  ctx.globalAlpha = 1;
  return x;
}
// the great blue heron: standing on a pile cap (preening now and then), or gliding on broad slow wings
function drawHeron(t) {
  for (const h of HERON) {
    if (t < h.in || t >= h.gone) continue;
    const P = PILES[h.pile], cx = P.x, cy = P.top - 5;
    if (t >= h.land && t < h.off) {
      const preen = frac((t - h.land) / 8) < 0.2;
      R(cx - 1, cy + 1, 1, 4, '#4a4e58'); R(cx + 1, cy + 1, 1, 4, '#4a4e58');            // legs
      R(cx - 2, cy - 4, 5, 5, '#6a7a92'); R(cx - 2, cy - 4, 5, 1, '#9aa8c0'); R(cx + 2, cy - 2, 2, 2, '#4a5468');
      if (preen) { R(cx - 2, cy - 6, 2, 3, '#c8ccd4'); R(cx - 3, cy - 3, 1, 1, '#d8b040'); }
      else { R(cx - 2, cy - 9, 1, 6, '#c8ccd4'); R(cx - 2, cy - 10, 3, 2, '#e8eaee'); R(cx + 1, cy - 10, 3, 1, '#d8b040'); R(cx - 2, cy - 10, 2, 1, '#2a2a34'); }
      continue;
    }
    const arriving = t < h.land, u = arriving ? smooth(h.in, h.land, t) : smooth(h.off, h.gone, t);
    const sx = arriving ? cx + h.from * 300 : cx, sy = arriving ? cy - 40 : cy, ex = arriving ? cx : cx - h.from * 300, ey = arriving ? cy : cy - 50;
    const x = Math.round(lerp(sx, ex, u)), y = Math.round(lerp(sy, ey, u) - Math.sin(Math.PI * u) * 8), d = arriving ? -h.from : -h.from;
    const flap = wave(t, 120) > 0;
    R(x - 4, y, 9, 2, '#6a7a92'); R(x + d * 5, y, 2, 1, '#c8ccd4'); R(x + d * 7, y, 2, 1, '#d8b040');   // body, tucked neck, bill
    R(x - d * 6, y + 1, 3, 1, '#4a4e58');                                                               // trailing legs
    for (let k = 0; k < 7; k++) R(x - 3 + k, y + (flap ? -1 - (k < 2 || k > 4 ? 1 : 0) : 2 + (k < 2 || k > 4 ? 1 : 0)), 1, 1, '#5a6880');
  }
}
// the sculler: a long thin shell, the rower swinging, two oars sweeping, a white lamp on the bow
function drawScull(t, crowd) {
  if (crowd <= 0) return;
  for (const S of SCULL) {
    if (t < S.t0 || t >= S.t1) continue;
    const u = (t - S.t0) / (S.t1 - S.t0), x = Math.round(S.dir > 0 ? lerp(-30, 500, u) : lerp(500, -30, u)), wl = 178, d = S.dir;
    const ph = frac(t * 96 / LOOP), lean = ph < 0.5 ? Math.round(lerp(-2, 2, ph * 2)) : Math.round(lerp(2, -2, (ph - 0.5) * 2));
    R(x - 10, wl - 1, 20, 1, '#d8d4c8'); R(x - 9, wl, 18, 1, '#5a5650');
    R(x + d * 10, wl - 1, 1, 1, '#ffffff'); halo(HL.bike, x + d * 10, wl - 1, 0.8);
    R(x - 1 + d * lean, wl - 7, 3, 5, '#c84a3e'); R(x - 1 + d * lean, wl - 10, 3, 3, '#e0aa80'); R(x - 1 + d * lean, wl - 10, 3, 1, '#3c2418');
    const oy = ph < 0.5 ? wl + 1 : wl - 2;
    ctx.fillStyle = '#c8c4b8';
    for (let k = 0; k < 8; k++) { ctx.fillRect(x + d * (lean + 2 - k), Math.round(lerp(wl - 4, oy, k / 7)) - 0, 1, 1); }
    if (ph < 0.5) { ctx.globalAlpha = 0.5; R(x - d * 7, wl + 1, 3, 1, '#a8c0c8'); ctx.globalAlpha = 1; }
  }
}

/* ================= People on the Esplanade =================
   rank: drawn while rank < crowd (Settings, People); rank 0 is there at every level above 0 (the busker, the cook, the
   sculler, the cart's customer of the moment). CROWD 0.5 shows the busker, the bench couple, the two at the rail, the
   pair at the picnic table, two cyclists, the dog walker, the walking couple and the cart's queue.                   */
const CAST_LOOK = {
  busker: look(20, 2, 0, '#4a6a3a', '#3a3a58', { hat: '#2a2a32' }),
  sitA: look(20, 2, 0, '#3a64c0', '#3a3a58'), sitB: look(19, 0, 4, '#c8a058', '#22202c', { long: 3 }),
  shoot: look(21, 1, 1, '#c8404a', '#33466e'), friend: look(19, 0, 3, '#e0b840', '#22202c', { long: 4 }),
  cook: look(20, 1, 0, '#e8e2d4', '#22202c', { apron: '#c84a3e', hat: '#e8e2d4' }),
  eatA: look(19, 0, 5, '#c0c8d8', '#33466e'), eatB: look(19, 3, 1, '#d87aa0', '#22202c', { long: 2 }),
  leaner: look(21, 1, 2, '#5a5a6a', '#22202c', { hat: '#8a2a34' }),
};
const CUST = [look(20, 2, 1, '#7a5ac0', '#4a3a30'), look(21, 0, 3, '#2a9a8a', '#22202c', { long: 3 }), look(20, 3, 0, '#e0b840', '#33466e'),
  look(19, 1, 4, '#c8404a', '#22202c', { long: 4 }), look(21, 0, 5, '#5aa8d0', '#3a3a58', { hat: '#3a3a44' })];
const WALKERS = [
  { kind: 'bike', dir: 1, lane: 206, n: 6, dur: 13, ph: 0.2, rank: 0.12, P: look(20, 3, 0, '#e0803a', '#33466e', { hat: '#3a3a44' }), frame: '#c8a040' },
  { kind: 'bike', dir: -1, lane: 203, n: 5, dur: 14, ph: 0.4, rank: 0.32, P: look(20, 0, 1, '#2a9a8a', '#22202c', { hat: '#c84a3e' }), frame: '#5a8ad0' },
  { kind: 'bike', dir: 1, lane: 203, n: 4, dur: 15, ph: 0.72, rank: 0.55, P: look(20, 1, 2, '#e8e2d4', '#3a3a58', { hat: '#2a6a4a' }), frame: '#d8d8e0' },
  { kind: 'jog', dir: -1, lane: 206, n: 4, dur: 26, ph: 0.22, rank: 0.62, P: look(20, 2, 0, '#5aa8d0', '#22202c') },
  { kind: 'dog', dir: 1, lane: 203, n: 2, dur: 76, ph: 0.55, rank: 0.25, P: look(21, 0, 2, '#3a6a4a', '#3a3a58', { scarf: '#c8a058' }), umb: '#3a6ac0' },
  { kind: 'pair', dir: -1, lane: 206, n: 2, dur: 84, ph: 0.1, rank: 0.42, P: look(21, 1, 0, '#8a2a34', '#22202c'), Q: look(19, 0, 3, '#d8c8a8', '#3a3a58', { long: 4 }), umb: '#c84a5a' },
  { kind: 'phone', dir: 1, lane: 206, n: 3, dur: 54, ph: 0.85, rank: 0.8, P: look(20, 3, 1, '#7a5ac0', '#33466e') },
];
let DL = [];
const put = (y, f) => DL.push({ y, f });
const shadow = (x, fy, w = 7) => { ctx.globalAlpha = 0.35; R(Math.round(x - w / 2), fy + 1, w, 1, '#0a0c14'); ctx.globalAlpha = 1; };
function walkerAt(w, t) {
  const q = t * w.n / LOOP + w.ph, u = frac(q) * (LOOP / w.n) / w.dur;
  if (u >= 1) return null;
  return w.dir > 0 ? lerp(-24, 504, u) : lerp(504, -24, u);
}
function drawWalker(w, x, t) {
  const fy = w.lane, st = step(t, 768) & 1;
  if (w.kind === 'bike') {
    shadow(x, fy, 16);
    const lamp = drawCyclist(x, fy, w.dir, { ...w.P }, frac(t * 192 / LOOP));
    halo(HL.bike, lamp[0] + w.dir * 3, lamp[1], 0.9);
    return;
  }
  if (w.kind === 'jog') {
    shadow(x, fy);
    const hy = person(x, fy, w.P, { view: 2, dir: w.dir, step: step(t, 1536) & 1, aR: 3 });
    R(x + w.dir, hy + 1, 1, 1, '#fff4d0'); halo(HL.bike, x + w.dir * 2, hy + 1, 0.8);
    return;
  }
  if (w.kind === 'dog') {
    const dx = x + w.dir * 13;
    shadow(x, fy); shadow(dx, fy, 9);
    drawDog(dx, fy, w.dir, st);
    const hy = person(x, fy, w.P, { view: 2, dir: w.dir, step: st });
    drawUmbrella(x, hy, w.umb);
    bres((px, py) => R(px, py, 1, 1, '#1a1a22'), x + w.dir * 2, fy - 9, dx - w.dir * 4, fy - 5);   // the lead
    return;
  }
  if (w.kind === 'pair') {
    const x2 = x - w.dir * 8;
    shadow(x, fy); shadow(x2, fy);
    person(x2, fy, w.Q, { view: 2, dir: w.dir, step: 1 - st });
    const hy = person(x, fy, w.P, { view: 2, dir: w.dir, step: st });
    drawUmbrella(x - w.dir * 3, hy - 1, w.umb, 17);
    return;
  }
  shadow(x, fy);
  const hy = person(x, fy, w.P, { view: 2, dir: w.dir, step: st });
  R(x + w.dir * 2, hy + 3, 1, 2, '#8ad8e8'); halo(HL.phone, x + w.dir * 2, hy + 3, 0.6);
}
// the cart's queue: a customer every 12 s, served, off to the right with a steaming box; the next steps up and a new
// one comes along out of the dark
const CART_X = (CART.x0 + CART.x1) >> 1, SERVE = 12;
const custRank = c => (c % 2 ? 0.45 : 0.05);
function drawQueue(t, crowd) {
  const q = t / SERVE, c = Math.floor(q), u = q - c, n = LOOP / SERVE, id = k => ((k % n) + n) % n;
  const handing = u > 0.5 && u < 0.7;
  // the one at the hatch, then walking off
  if (custRank(id(c)) < crowd) {
    const P = CUST[id(c) % CUST.length];
    if (u < 0.7) put(207, () => { shadow(CART_X - 4, 207); person(CART_X - 4, 207, P, { aR: handing ? 3 : 0 }); });
    else {
      const x = lerp(CART_X - 4, 500, smooth(0.7, 1, u));
      put(207, () => {
        shadow(x, 207);
        const hy = person(x, 207, P, { view: 2, dir: 1, step: step(t, 768) & 1, aR: 3 });
        R(Math.round(x) + 3, hy + 7, 3, 2, '#e8e0d0'); R(Math.round(x) + 4, hy + 5 - (step(t, 480) & 1), 1, 1, '#c8ccd8');
      });
    }
  }
  // the next in line, stepping up when the hatch is free
  if (custRank(id(c + 1)) < crowd) {
    const P = CUST[id(c + 1) % CUST.length], x = lerp(CART_X - 18, CART_X - 4, smooth(0.72, 0.86, u)), moving = u > 0.72 && u < 0.86;
    put(207, () => { shadow(x, 207); person(x, 207, P, moving ? { view: 2, dir: 1, step: step(t, 768) & 1 } : { aL: 2 }); });
  }
  // a new one coming along the walkway out of the dark
  if (u > 0.55 && custRank(id(c + 2)) < crowd) {
    const P = CUST[id(c + 2) % CUST.length], x = lerp(CART_X - 84, CART_X - 18, smooth(0.55, 1, u));
    put(207, () => { ctx.globalAlpha = smooth(0.55, 0.7, u); shadow(x, 207); person(x, 207, P, { view: 2, dir: 1, step: step(t, 768) & 1 }); ctx.globalAlpha = 1; });
  }
  return handing;
}
function drawPeople(t, crowd) {
  DL = [];
  if (crowd > 0) {
    // the busker on his crate by the lamp, strumming, his case open for coins
    put(204, () => {
      const x = 66, fy = 204, strum = step(t, 960) & 1;
      R(x - 5, 197, 10, 7, '#4a3426'); R(x - 5, 197, 10, 1, '#6a4a34');
      R(x - 9, 205, 14, 2, '#1e1418'); R(x - 8, 205, 12, 1, '#7a2a3a');
      if (hash(7, step(t, 240)) < 0.5) R(x - 5 + (step(t, 240) % 7), 205, 1, 1, '#e8d080');
      person(x, 197, CAST_LOOK.busker, { sit: true, view: 1 });
      R(x - 2, 199 - 3, 2, 6, '#3a3a58'); R(x + 1, 199 - 3, 2, 6, '#3a3a58'); R(x - 2, 202, 2, 1, '#221a20'); R(x + 1, 202, 2, 1, '#221a20');
      R(x - 2, 194, 6, 4, '#a86a34'); R(x - 1, 193, 4, 6, '#a86a34'); R(x - 2, 194, 6, 1, '#c88a4a'); R(x, 195, 2, 2, '#2a1a14');   // the guitar's body on his lap
      bres((px, py) => R(px, py, 1, 1, '#5a3a24'), x - 2, 195, x - 10, 189);                               // its neck
      R(x + 2, 195 + strum, 1, 2, CAST_LOOK.busker.skin);
    });
    // the couple on the bench, backs to us, watching the sign; her head on his shoulder now and then
    if (0.1 < crowd) put(201, () => {
      const lean = frac(t / 40 + 0.2) < 0.45 ? -2 : 0;
      person(216, BENCH.y - 6, CAST_LOOK.sitB, { sit: true, lean });
      person(206, BENCH.y - 6, CAST_LOOK.sitA, { sit: true });
      R(BENCH.x0, BENCH.y - 9, BENCH.x1 - BENCH.x0 + 1, 1, '#7a5a40'); R(BENCH.x0, BENCH.y - 8, BENCH.x1 - BENCH.x0 + 1, 1, '#5a4030');
    });
    // two at the rail: she photographs the sign, he points at the stag now and then
    if (0.3 < crowd) put(199, () => {
      const shoot = frac(t / 24 + 0.3) < 0.6, point = frac(t / 30 + 0.6) < 0.2;
      person(268, 199, CAST_LOOK.shoot, { aR: shoot ? 2 : 0 });
      person(277, 199, CAST_LOOK.friend, { aL: point ? 1 : 0, lean: point ? -1 : 0 });
      if (shoot && frac(t / 24 + 0.3) > 0.55) halo(HL.phone, 270, 180, 0.9);
    });
    // a man leaning on the rail watching the river (Busy only)
    if (0.7 < crowd) put(199, () => person(140, 199, CAST_LOOK.leaner, { aL: 0 }));
    // the pair at the picnic table, eating
    if (0.2 < crowd) put(199, () => {
      person(462, 199, CAST_LOOK.eatA, { sit: true, view: 1, aR: frac(t / 8) < 0.3 ? 5 : 0 });
      person(472, 199, CAST_LOOK.eatB, { sit: true, aL: frac(t / 10 + 0.5) < 0.3 ? 5 : 0 });
    });
    for (const w of WALKERS) {
      if (w.rank >= crowd) continue;
      const x = walkerAt(w, t);
      if (x != null) put(w.lane, () => drawWalker(w, Math.round(x), t));
    }
  }
  // the cart itself sorts with the people: the cook in the hatch, steam off the pot
  const handing = crowd > 0 ? drawQueue(t, crowd) : false;
  put(206, () => {
    ctx.drawImage(CART_C, 0, 0);
    if (crowd > 0) {
      ctx.save(); ctx.beginPath(); ctx.rect(CART.x0 + 10, CART.top + 16, CART.x1 - CART.x0 - 24, 15); ctx.clip();
      person(CART_X - 2, CART.top + 40, CAST_LOOK.cook, { view: 1, aR: handing ? 3 : 0, aL: frac(t / 6) < 0.5 ? 3 : 0 });
      ctx.restore();
    }
    for (let k = 0; k < 6; k++) {                                    // steam off the pot, drifting up past the awning
      const { cyc, u } = cycle(t, 60, k / 6), x = CART.x0 + 16 + Math.round(hash(k, cyc, 3) * 8 + Math.sin(u * 6 + k) * 2), y = Math.round(CART.top + 22 - u * 16);
      ctx.globalAlpha = 0.35 * (1 - u); R(x, y, 2, 1, '#d8dce4'); ctx.globalAlpha = 1;
    }
  });
  put(210, () => { R(LAMP.x, LAMP.y + 40, 1, 70, '#4a4e5a'); R(LAMP.x + 1, LAMP.y + 40, 1, 70, '#22242e'); });   // the lamp post's foot
  DL.sort((a, b) => a.y - b.y);
  for (const d of DL) d.f();
}

/* ================= Old Town's windows, switching on and off ================= */
const WINS = [];
{
  const r = rng(77);
  for (const b of TOWN) for (let k = 0; k < 3; k++) {
    const x = b.x0 + 1 + 4 * Math.floor(r() * Math.max(1, (b.x1 - b.x0 - 3) / 4)), y = b.top + 3 + b.st * Math.floor(r() * 3) + 2;
    if (y < 150) WINS.push({ x, y, ph: r(), n: 6 + Math.floor(r() * 18) });
  }
}
function drawWindows(t) {
  for (const w of WINS) R(w.x, w.y, 2, 2, hash(w.x, step(t, w.n)) < 0.55 ? '#ffd890' : '#1e1a2c');
}

/* ================= What keeps the still parts breathing ================= */
// thin moonlit wisps drifting west (no outlines, dithered into the sky), tileable so they scroll
const WISPS = [{ seed: 3, y: 6, h: 14, n: 2, k: 1 }, { seed: 7, y: 24, h: 12, n: 2, k: 3 }, { seed: 5, y: 44, h: 14, n: 2, k: 2 }, { seed: 9, y: 74, h: 12, n: 2, k: 1 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = 50 + r() * 70;
    for (let k = 0; k < 6; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.4 + r() * 0.25), rx: 12 + r() * 16, ry: 1.6 + r() * 1.8 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    if (v > 0.55) p.put(x, y, v > 0.8 ? '#28325e' : '#202a56');
    else if (v > 0.2 && (v - 0.2) * 2.6 > bayer(x, y)) p.put(x, y, '#1a2350');
  }
  return { c: p.done(), y: def.y, k: def.k };
}
// low cloud caught on the West Hills, drifting along the ridge (tileable)
function renderHillMist() {
  const h = 26, p = painter(W, h), r = rng(44), blobs = [];
  for (let k = 0; k < 9; k++) blobs.push({ x: r() * W, y: 10 + r() * 8, rx: 26 + r() * 34, ry: 4 + r() * 4 });
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    if (v > 0.15 && v * 0.75 > bayer(x, y) + 0.08) p.put(x, y, v > 0.6 ? '#3a4660' : '#2c3650');
  }
  return p.done();
}
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
// planes letting down towards PDX: a slow crossing, red and green lamps, a white blink
const PLANES = [{ t0: 18, t1: 82, x0: -20, x1: 500, y0: 36, y1: 24 }, { t0: 132, t1: 196, x0: 500, x1: -20, y0: 58, y1: 46 }];
function drawPlanes(t) {
  for (const P of PLANES) {
    if (t < P.t0 || t >= P.t1) continue;
    const u = (t - P.t0) / (P.t1 - P.t0), x = Math.round(lerp(P.x0, P.x1, u)), y = Math.round(lerp(P.y0, P.y1, u));
    R(x - 2, y, 5, 1, '#3a3e52'); R(x - 1, y, 1, 1, '#ff4a3a'); R(x + 1, y, 1, 1, '#4aff8a');
    if (step(t, 160) % 2 === 0) { R(x, y - 1, 1, 1, '#ffffff'); halo(HL.bike, x, y - 1, 0.5); }
  }
}
// windows up and down the towers and Old Town switching on and off; lamp-lit crowns stirring; the hills' houses
const TWINKLES = [];
{
  const r = rng(131);
  const towers = [[197, 210, 54, 130], [174, 186, 72, 128], [160, 171, 94, 125], [183, 196, 86, 125], [212, 224, 92, 128],
    [BIGPINK.x0 + 1, BIGPINK.x1 - 6, BIGPINK.top + 3, 125], [SIGN.x - 2, SIGN.x + SIGN.w + 2, ROOF + 4, 134], [362, 480, 116, 134]];
  for (const [x0, x1, y0, y1] of towers) for (let k = 0; k < 9; k++) {
    const x = x0 + Math.floor(r() * (x1 - x0)), y = y0 + Math.floor(r() * (y1 - y0));
    TWINKLES.push({ x, y, n: 4 + Math.floor(r() * 20), on: r() < 0.5 ? '#ffd890' : '#f2b060' });
  }
  for (let k = 0; k < 40; k++) {                                         // house lights on the hills
    const x = Math.floor(r() * W), y = Math.round(ridgeY(x) + 4 + r() * 18);
    if (y < 118 && !(x > SIGN.x - 4 && x < SIGN.x + SIGN.w + 4)) TWINKLES.push({ x, y, n: 10 + Math.floor(r() * 30), on: '#d8a050' });
  }
}
function drawTwinkles(t) {
  for (const w of TWINKLES) if (hash(w.x, w.y, step(t, w.n)) < 0.6) R(w.x, w.y, 1, 1, w.on);
}
const CROWNS = Array.from({ length: 70 }, (_, i) => ({ x: 90 + Math.floor(hash(i, 141) * 390), y: 138 + Math.floor(hash(i, 142) * 10), n: 120 + Math.floor(hash(i, 143) * 200) }));
function drawCrowns(t) {
  for (const c of CROWNS) if (hash(c.x, step(t, c.n)) < 0.3) R(c.x, c.y, 1, 1, '#4a5a3e');
}
// leaves skittering along the wet walkway in the wind, a hop now and then
const SKITTER = Array.from({ length: 14 }, (_, i) => ({ n: 6 + (i % 5), ph: hash(i, 151), y: 212 + Math.floor(hash(i, 152) * 54), col: ['#c89a2a', '#b8742a', '#d8b040'][i % 3] }));
function drawSkitter(t) {
  for (const L of SKITTER) {
    const { u } = cycle(t, L.n, L.ph), x = Math.round(lerp(-10, 490, u)), hop = Math.max(0, Math.sin(u * 40)) > 0.85 ? 1 : 0;
    R(x, L.y - hop, 2, 1, L.col); R(x + 1, L.y - hop + 1, 1, 1, mixHex(L.col, '#1e202a', 0.4));
  }
}

/* ================= Frame compositor (back to front) ================= */
// the sign's colours laid on the river as broken streaks: amber and green under the words, white under the stag,
// red under OLD TOWN
const SIGN_STREAKS = [[SU(20), 4, '#ffb84a', 0.4], [SU(34), 5, '#3cf070', 0.32], [SU(50), 6, '#ffb84a', 0.4],
  [SU(66), 4, '#3cf070', 0.3], [SU(54), 3, '#e8f4ff', 0.2], [SU(42), 8, '#ff4a34', 0.3], [SU(10), 2, '#ff9a3a', 0.2], [SU(80), 2, '#ff9a3a', 0.2]];
const pxPut = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1); };
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS, dz = drizzleAt(t);
  // sky, stars, moon
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  ctx.globalAlpha = 0.85; for (const L of WISP_C) drawScroll(L.c, L.y, L.k, t); ctx.globalAlpha = 1;
  drawPlanes(t);
  halo(HL.haze, 260, 150, 1);
  // the West Hills and their masts' red lamps, breathing slowly; downtown; Old Town and Union Station
  ctx.drawImage(HILLS_C, 0, 0);
  MASTS.forEach((m, i) => {
    const a = 0.55 + 0.45 * wave(t, 80, i * 0.31);
    for (const y of [m.top, m.top + Math.round((ridgeY(m.x) - m.top) * 0.5)]) { R(m.x, y, 1, 1, '#ff4a3a'); halo(HL.red, m.x, y, a * (y === m.top ? 1 : 0.6)); }
  });
  ctx.globalAlpha = 0.5; drawScroll(MIST_C, 96, 2, t); ctx.globalAlpha = 1;
  ctx.drawImage(DOWN_C, 0, 0);
  halo(HL.koin, 180, 64, 0.9);
  for (const x of [BIGPINK.x0 + 2, BIGPINK.x1 - 2]) { R(x, BIGPINK.top - 1, 1, 1, '#ff4a3a'); halo(HL.red, x, BIGPINK.top - 1, 0.5 + 0.4 * wave(t, 60, x * 0.01)); }
  ctx.drawImage(TOWN_C, 0, 0);
  drawWindows(t);
  drawTwinkles(t);
  halo(HL.union, UNION.x, UNION.top + 9, 0.8);
  if (wx.drizzle) drawDrizzle(0, t, lights, dz);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the Burnside Bridge: its traffic behind the balustrade, the deck and piers, the two leaves (lifting for the boat),
  // the gates, the tower windows and deck lamps
  drawTraffic(t);
  ctx.drawImage(BRIDGE_C, 0, 0);
  const a = liftAngle(t);
  if (a > 0.002) { leafPixels(pxPut, 0, a); leafPixels(pxPut, 1, a); } else ctx.drawImage(LEAF_C, 0, 0);
  drawGates(t);
  for (const T of TOWERS) halo(HL.tower, T.x, Math.round(deckY(T.x)) - T.h + 4, 0.8);
  for (const [x, h] of BR_LAMPS) {
    const y = Math.round(deckY(x)) - 1;
    R(x, y - h, 1, h, '#4a4c58'); R(x, y - h, h > 6 ? 3 : 2, 1, '#5a5c68'); R(x + (h > 6 ? 2 : 1), y - h + 1, 1, 1, '#fff0c8');
    halo(HL.brLamp, x + 2, y - h + 1, 0.8);
  }
  // the White Stag Building, then the sign: its glow, its tubes and bulbs, a few bulbs brighter at a time
  ctx.drawImage(STAG_C, 0, 0);
  halo(HL.sign, SU(42), SV(52), 0.9); halo(HL.signGreen, SU(44), SV(50), 0.8);
  halo(HL.stag, SU(52), SV(12), 0.9); halo(HL.old, SU(32), SV(82), 0.8); halo(HL.old, SU(54), SV(82), 0.8);
  ctx.globalCompositeOperation = 'lighter'; ctx.drawImage(SIGN_GLOW.c, SIGN_GLOW.x, SIGN_GLOW.y); ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(SIGN_C, 0, 0);
  ctx.fillStyle = '#fff4c8';
  const sp = step(t, 1200);
  for (let i = 0; i < BULBS.length; i += 2) if (hash(i, sp, 17) < 0.05) ctx.fillRect(BULBS[i], BULBS[i + 1], 1, 1);
  // Waterfront Park: the trees, the globe lamps, the seawall; the billboard on its roof
  ctx.drawImage(PARK_C, 0, 0);
  drawCrowns(t);
  for (const x of PARK_LAMPS) halo(HL.park, x, 142, 0.85);
  if (billboard) drawBillboard(t, pitch);
  // the Willamette: base, the mirror of everything above, streaks under the lights; the boats; the sculler
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: KERB - WL, squash: 1.5, alpha: 0.34, amp: 0.7 });
  for (const [x, w, c, al] of SIGN_STREAKS) streak(ctx, t, x, w, c, al, x * 0.37, WL + 2, KERB);
  for (const x of PARK_LAMPS) streak(ctx, t, x, 3, '#ffc070', 0.26, x * 0.71, WL + 1, KERB);
  for (const [x] of BR_LAMPS) streak(ctx, t, x + 2, 2, '#ffe8b8', 0.18, x * 0.53, WL + 4, KERB);
  for (const T of TOWERS) streak(ctx, t, T.x, 3, '#ffd090', 0.2, T.x * 0.5, PIERS[0].foot, KERB);
  const cx = drawCruise(t);
  if (cx != null) streak(ctx, t, cx - 22, 30, '#ffd890', 0.24, 5, 162, KERB);
  const sb = sailAt(t);
  if (sb) drawSailboat(sb.x, sb.wl, sb.s, sb.a);
  if (wx.drizzle) { drawDrizzle(1, t, lights, dz); drawRings(t, dz); }
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawScull(t, crowd);
  // the walkway, the rail, the piles and the heron on one; the people, the cart and the busker, in feet order
  ctx.drawImage(WALK_C, 0, 0);
  drawHeron(t);
  halo(HL.sheen, 260, KERB + 18, 0.7);
  halo(HL.pool, LAMP.x + 1, 214, 0.8);
  halo(HL.hatch, CART_X - 4, CART.top + 26, 1);
  streak(ctx, t, CART_X - 4, 10, '#ffb860', 0.26, 11, CART.base + 2, H);
  for (const [px, w, c] of PUDDLES) streak(ctx, t, px, w, c, 0.3, px * 0.3, 222, 246);
  drawPeople(t, crowd);
  const sway = wave(t, 30);
  for (let x = CART.x1 - 6, i = 0; x < 472; x += 3, i++) {
    const u = (x - CART.x1 + 6) / (472 - CART.x1 + 6), y = Math.round(CART.top + 6 + u * 4 + Math.sin(Math.PI * u) * (3 + sway));
    R(x, y, 1, 1, '#2a2226'); R(x, y + 1, 1, 1, i % 3 ? '#ffd890' : '#ffeec0'); halo(HL.bulb, x, y + 1, 0.8);
  }
  reflect(ctx, t, { top: FEET + 3, rows: 34, squash: 1, alpha: 0.16, amp: 0.4, k1: 30, k2: 70 });
  ctx.drawImage(LEAVES_C, 0, 0);
  drawSkitter(t);
  for (const [px, w, c] of PUDDLES) streak(ctx, t, px + 40, w, c, 0.18, px * 0.7, 248, 266);
  halo(HL.lamp, LAMP.x + 1, LAMP.y + 3, 1); halo(HL.lampCore, LAMP.x + 1, LAMP.y + 2, 0.9);
  if (wx.drizzle) drawDrizzle(2, t, lights, dz);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}
// the sign's neon caught in the walkway's puddles
const PUDDLES = [[SU(30), 8, '#ffb84a'], [SU(44), 6, '#3cf070'], [SU(56), 6, '#ff4a34'], [118, 6, '#ffc070']];

const CROWD = 0.5;
export default {
  id: 'portland',
  name: 'Portland',
  tagline: 'Neon stag over the Willamette',
  timeZone: 'America/Los_Angeles',
  accent: '#3f7d3a',
  focusX: SIGN.x + (SIGN.w >> 1),
  landmark: [SIGN.x - 1, SIGN.x + SIGN.w],          // the "Portland Oregon" sign, outline to outline
  landmarkTop: SIGN.y - 1,                          // the stag's antler tips
  billboard: BB,
  crowd: CROWD,
  spots: [                                          // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: SIGN.x, y: SIGN.y, w: SIGN.w, h: SIGN.h },                 // the "Portland Oregon" sign
    { kind: 'window', x: TOWERS[0].x - 6, y: 87, w: 13, h: 24 },                      // the bridge's east tower house
    { kind: 'water', x: SIGN.x + 8, y: 166, w: 80, h: 20 },                           // the Willamette under the sign
  ],
  ambience: { rain: 0.32, water: 0.4, traffic: 0.16, wind: 0.1, crowd: 0.06 },
  events: [
    { t: 36, type: 'boat', dur: 56, pan: [1, -0.4], gain: 0.5 },
    { t: LIFT.horn, type: 'horn', x: 300, gain: 0.45 }, { t: LIFT.horn + 2.2, type: 'horn', x: 290, gain: 0.3 },
    { t: LIFT.bell, type: 'bell', x: 115, gain: 0.5 }, { t: LIFT.bell + 1.5, type: 'bell', x: 115, gain: 0.5 },
    { t: LIFT.bell + 3, type: 'bell', x: 115, gain: 0.5 }, { t: LIFT.down0, type: 'bell', x: 115, gain: 0.45 },
    { t: LIFT.down0 + 1.5, type: 'bell', x: 115, gain: 0.45 },
    { t: CRUISE.t0, type: 'boat', dur: 56, pan: [-0.2, 1], gain: 0.55 }, { t: 156, type: 'horn', x: 230, gain: 0.5 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
